import { compileAmbient, hasCompileAmbient } from '../../janus-engine/compile/compile-ambient';
import { requireInventoryFloor } from '../../janus-engine/compile/inventory-facet';
import { statusIsPaused } from '../../janus-engine/statuses/status-clock';
// Browser FT3D semantic-minimap perception rules. Keep ordinary status semantics in lockstep with
// ft3d_minimap_projection.php.
import {paintedWallOpaqueGeometries, paintedWallObscuresRay} from '../../shared/painted-wall-visibility';
import type {PaintedWallGeometry} from '../../shared/painted-wall-geometry';

import {
  encounterCreatureField,
  encounterField,
  itemsCol,
  layerEntriesOf,
  layerListEntryUuid,
  layerOf,
  layerValue,
  posCoord,
  primaryPictureOf,
  reachOf,
  senseValue,
  sizeIdOf,
} from '../../shared/array-types';
import { combatCenterDistanceFt } from '../../../../ft3d/engine/ft3d-combat-distance-core.js';
import {
  getSwarmTemplateForCreature,
  swarmOccupiedWidthFt,
  swarmVRatio01,
  swarmWidthLanesForCreature,
} from '../../shared/swarm-templates';
import {
  ambientLightAt,
  boxIsRoofed,
  environmentIsUnderground,
  insideUnion,
  segmentHit,
  shapeLightFixtures,
} from '../../../../ft3d/engine/ft3d-shape-core.js';
import { floorBandForPosition } from '../../encounter-creation-engine/campaign-placement-authority';
import {
  CELL_LIGHT_TIER,
  cellLightTierAt,
  decodeCellLightFloor,
  isFt3dCellLightGrid,
  type Ft3dCellLightGrid,
  type Ft3dCellLightFloor,
} from '../../encounter-creation-engine/ft3d-cell-light';
import {
  buildExplorationCellRegistry,
  type Ft3dExplorationCell,
  type Ft3dExplorationShape,
} from '../../encounter-creation-engine/ft3d-exploration';
import type {
  Ft3dDoorPeekBinding,
  Ft3dDoorPeekBlock,
  Ft3dMinimapVisibility,
  Ft3dTeamExplorationPreview,
  Ft3dTeamExplorationState,
} from '../../encounter-creation-engine/ft3d-minimap';

export const PERCEPTION_LIGHT = Object.freeze({
  bright: '0CC8A592-CF4C-7BE4-DD0C-17A41D690A05',
  dim: '61D1498F-9175-6906-4B26-8356C28B28C1',
  pitch: '99D3E371-AC3F-7839-28E7-770FE50D24C1',
  magicalDarkness: '67F42B5B-E3D1-0818-0197-1F36B20FC630',
});

export const PERCEPTION_STATUS = Object.freeze({
  blinded: 'CEFF8A40-503C-FDA3-41B3-43DC06BF762D',
  obscured: 'E2054884-038E-3D6E-56C5-19ABBFED8379',
  hidden: '47667406-1B72-9D60-310B-D8CF6BFE397D',
  invisible: '84B91EEB-2FA6-D1CB-1347-75BFBDA2A6DD',
  greaterInvisible: 'B6232175-C357-9A97-0ACA-7ECC67563635',
  ethereal: '2A2845CD-949F-06E2-E6B2-98CFD5603241',
});


const SIZE_WIDTHS_FT: Readonly<Record<string, number>> = Object.freeze({
  '13456FEC-A2EE-DB46-5829-0EB2F627E1B7': 2.5,
  'AAC1F466-7A8B-C593-1ADC-FC402B8909D7': 3.75,
  'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC': 5,
  '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD': 10,
  'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C': 15,
  'CEA2946E-D395-46EA-A6E7-74D21EA2376E': 20,
  'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68': 25,
  '4C33FD22-5382-15DA-1C46-F57F14378AFD': 30,
});

type Row = Record<string, unknown>;

export interface PerceptionRange { min: number; max: number }

export interface PerceptionBox {
  id?: string;
  x: PerceptionRange;
  y: PerceptionRange;
  floor: number;
  ceiling: number;
  environment?: string;
  presetId?: string;
  creation?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PerceptionOpening {
  id?: string;
  plane?: { axis?: 'x' | 'y'; at?: number };
  span?: PerceptionRange;
  z?: PerceptionRange;
  passable?: boolean;
  transparent?: boolean;
  exterior?: boolean;
  kind?: string;
  creation?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PerceptionShape {
  boxes: PerceptionBox[];
  openings?: PerceptionOpening[];
  [key: string]: unknown;
}

export interface PerceptionPosition { x: number; y: number | null; z: number }

export interface PerceptionSource {
  x: number;
  /** The server source shape calls floor-plane y `z` for legacy helper compatibility. */
  z: number;
  elevation?: number | null;
  radius: number;
  magical_light?: unknown;
  sunlight_light?: unknown;
  fixture?: string;
}

export interface PerceptionLightContext {
  opaquePaintedWalls?: PaintedWallGeometry[];
  override: string | null;
  effective: string;
  underground: boolean;
  windows: Array<{ x: number; y: number; boxId: string; light: string }>;
  authored: boolean;
}

export interface VisibilityCell {
  id: string;
  roomId: string;
  x: PerceptionRange;
  y: PerceptionRange;
  floor: number;
  ceiling: number;
}

export interface PerceptionLightFloor {
  floor: number;
  originX: number;
  originY: number;
  columns: number;
  rows: number;
  tiers: number[];
  cellIndex: Record<string, number>;
  cellTiers: Record<string, number>;
}

export interface PerceptionInput {
  encounterArr: Record<string, unknown>;
  shape: PerceptionShape;
  canManage: boolean;
  viewerCreatureIds: string[] | null;
  light?: { override?: string | null; effective?: string; underground?: boolean };
  fixtureLightRadius?: number;
  /** Exact server-compatible physical epoch. Supplying it enables cache reuse. */
  physicalEpoch?: string;
}

export interface PerceptionResult {
  authority: 'gm-complete' | 'see-with-senses-plus-shape-los';
  view: 'gm' | 'player';
  viewerCreatureIds: string[];
  visibleCreatureIds: string[];
  visibleInventoryIds: string[];
  visibleRoomIds: string[];
  visibilityCellSizeFt: 5;
  visibleRoomCells: VisibilityCell[];
  lightField: { floors: Record<string, PerceptionLightFloor> };
}

export interface ObservableLightSource {
  x: number;
  y: number;
  z: number;
  radius: number;
  magical: boolean;
}

export interface PerceptionContextSeed {
  schema: 'ft.ft3d.perception-context.v1';
  override: string | null;
  effective: string;
  underground: boolean;
  fixtureLightRadius: number;
  queryOnly?: true;
  writes?: 0;
}

export interface ClientPerceptionInput extends Omit<PerceptionInput, 'physicalEpoch'> {
  doorStates?: Ft3dMinimapVisibility['doorStates'] | [];
  shapeSource?: 'stored' | 'derived-one-box';
  /** Last accepted server/client revision for this view. The next local revision is strictly higher. */
  minimumLightRevision?: number;
  /** Last server-issued durable state; local compute never manufactures this state. */
  teamExplorationState?: Ft3dTeamExplorationState;
  /**
   * Keyhole peek (DAFT3D S25): the held SERVER block plus what this browser currently knows. The block
   * is merged only while its binding still matches (mergeServerPeek) — the local lane never computes a
   * peek of its own.
   */
  serverPeek?: { block: Ft3dDoorPeekBlock; current: DoorPeekCurrent } | null;
}

/** What the browser knows right now, compared against a peek block's server binding. */
export interface DoorPeekCurrent {
  encounterId: string;
  viewerId: string;
  openingId: string;
  peekId: string;
  doorRevision: number;
  viewerPos: { x: number; y: number; z: number } | null;
}

const DOOR_PEEK_WITNESS_FT = 0.01;

/** Does a server peek binding still describe this browser's state? (Codex consult #1, findings 6/7.) */
export function doorPeekBindingMatches(binding: Ft3dDoorPeekBinding | undefined, current: DoorPeekCurrent): boolean {
  if (!binding || !current.viewerPos) return false;
  const same = (left: string, right: string): boolean => left.trim().toUpperCase() === right.trim().toUpperCase() && left.trim() !== '';
  return same(binding.encounterId, current.encounterId)
    && same(binding.viewerId, current.viewerId)
    && binding.openingId === current.openingId
    && same(binding.peekId, current.peekId)
    && Number(binding.doorRevision) === Number(current.doorRevision)
    && Math.abs(Number(binding.viewerPos.x) - current.viewerPos.x) <= DOOR_PEEK_WITNESS_FT
    && Math.abs(Number(binding.viewerPos.y) - current.viewerPos.y) <= DOOR_PEEK_WITNESS_FT
    && Math.abs(Number(binding.viewerPos.z) - current.viewerPos.z) <= DOOR_PEEK_WITNESS_FT;
}

/**
 * Union a server peek block into a BASE caster projection — only while the binding matches, else the
 * base comes back untouched. Twin of the union in ft3d_build_minimap_projection(): cells deduped by id,
 * rooms from the block's cone-filtered cells, creatures and inventory added. The union never mutates
 * the held base, so revoked peek cells are absent from the next result.
 */
export function mergeServerPeek(result: PerceptionResult, block: Ft3dDoorPeekBlock, current: DoorPeekCurrent): PerceptionResult {
  if (result.view !== 'player' || block.ok !== true || block.schema !== 'ft.ft3d.door-peek.v1'
    || block.openingId !== block.binding?.openingId
    || !doorPeekBindingMatches(block.binding, current)
    || !result.viewerCreatureIds.some(id => id.toUpperCase() === current.viewerId.trim().toUpperCase())) return result;
  const known = new Set(result.visibleRoomCells.map(cell => cell.id));
  const cells = [...result.visibleRoomCells];
  for (const cell of block.cells) if (!known.has(cell.id)) { known.add(cell.id); cells.push(cell); }
  return {
    ...result,
    visibleRoomCells: cells,
    visibleRoomIds: sortUnique([...result.visibleRoomIds, ...block.roomIds]),
    visibleCreatureIds: sortUnique([...result.visibleCreatureIds, ...block.creatureIds]),
    visibleInventoryIds: sortUnique([...result.visibleInventoryIds, ...block.inventoryIds]),
  };
}

/**
 * Explicit mutable memo owned by one encounter engine. Pure rules remain free of ambient state;
 * replacing the cache is sufficient to discard every derived value.
 */
interface HeldTargetVisibility {
  key: string;
  visible: boolean;
  rooms: string[];
}
interface HeldPerception {
  encounterId: string;
  viewKey: string;
  targets: Map<string, HeldTargetVisibility>;
  roomCounts: Map<string, number>;
  roomsKey: string;
  rooms: { roomIds: string[]; cells: VisibilityCell[] };
  result: PerceptionResult;
}
type CollectedSources = ReturnType<typeof collectLightAndDarkSources>;

export interface PerceptionCache {
  flight: Promise<Ft3dMinimapVisibility> | null;
  geometryKey: string;
  sources: { key: string; value: CollectedSources } | null;
  held: HeldPerception | null;
  packed: { field: PerceptionResult['lightField']; cells: VisibilityCell[]; complete: boolean; value: Ft3dCellLightFloor[] } | null;
  client: Ft3dMinimapVisibility | null;
  observable: { key: string; creatures: string[]; inventories: string[]; cells: VisibilityCell[];
    value: ObservableLightSource[] } | null;
  wire: { packed: Ft3dCellLightFloor[]; sources: ObservableLightSource[]; key: string;
    epoch: string; grid: Ft3dCellLightGrid } | null;
  physicalEpoch: string;
  lightField: PerceptionResult['lightField'] | null;
  visibilityCellsByRoom: Map<string, VisibilityCell[]>;
  cellGeometryByRoom: Map<string, string>;
  fixtures: { key: string; value: PerceptionSource[] } | null;
  boxAtByCellId: Map<string, PerceptionBox | null>;
  revisionsByView: Map<string, number>;
  explorationRegistry: {
    shapeJson: string;
    value: Awaited<ReturnType<typeof buildExplorationCellRegistry>>;
  } | null;
  teamPreviews: Map<string, Ft3dTeamExplorationPreview>;
}

export function createPerceptionCache(): PerceptionCache {
  return {
    flight: null, geometryKey: '', sources: null, held: null, packed: null, client: null, observable: null, wire: null,
    physicalEpoch: '',
    lightField: null,
    visibilityCellsByRoom: new Map(),
    cellGeometryByRoom: new Map(), fixtures: null,
    boxAtByCellId: new Map(),
    revisionsByView: new Map(),
    explorationRegistry: null,
    teamPreviews: new Map(),
  };
}

export interface SensesSnapshot {
  lair: boolean;
  blindsight: number;
  fiendsight: number;
  ghostsight: number;
  nightsight: number;
  spellsight: number;
  tremorsense: number;
  truesight: number;
  windsense: number;
  blindsightBlind: boolean;
  blinded: boolean;
}

export function recordOf(value: unknown): Row | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
}

function rowsOf(value: unknown): Row[] {
  if (!Array.isArray(value)) return [];
  const rows: Row[] = [];
  for (const candidate of value) {
    const row = recordOf(candidate);
    if (row) rows.push(row);
  }
  return rows;
}

function scalar(value: unknown): unknown {
  if (Array.isArray(value)) return value[0];
  const row = recordOf(value);
  return row && Object.prototype.hasOwnProperty.call(row, 0) ? row[0] : value;
}

export function numberOf(value: unknown, fallback = 0): number {
  const number = Number(scalar(value));
  return Number.isFinite(number) ? number : fallback;
}

export function stringOf(value: unknown): string {
  return String(scalar(value) ?? '');
}

export function truthyPhp(value: unknown): boolean {
  const unwrapped = scalar(value);
  return !(unwrapped === null || unwrapped === undefined || unwrapped === false || unwrapped === 0
    || unwrapped === '0' || unwrapped === '');
}

/** PHP ft3d_minimap_row_scalar, including first-containing-bucket wins. */
export function rowScalar(row: Row, key: string): unknown {
  const containers: Row[] = [row];
  for (const bucket of ['encounter_creatures', 'inventory', 'inventories', 'player_characters', 'items', 'item_layer_types', 'position']) {
    const container = recordOf(row[bucket]);
    if (container) containers.push(container);
  }
  for (const container of containers) {
    if (Object.prototype.hasOwnProperty.call(container, key)) return scalar(container[key]);
  }
  return null;
}

export function entityId(row: Row, kind: 'creature' | 'inventory'): string {
  return stringOf(rowScalar(row, kind === 'inventory' ? 'inventory_id' : 'encounter_creature_id')).trim();
}

export function playerPermission(row: Row): boolean {
  const permissions = recordOf(row.permissions);
  const value = scalar(permissions?.player ?? false);
  return value === true || value === 1 || value === '1';
}

export function privateRow(row: Row, kind: 'creature' | 'inventory'): boolean {
  return numberOf(rowScalar(row, kind === 'inventory' ? 'inventory_private' : 'encounter_creature_private')) === 1;
}

/** PHP position reader: unlike ordinary client positioning, missing/non-numeric y remains null. */
export function position(row: Row): PerceptionPosition {
  const rawY = rowScalar(row, 'y_pos');
  const y = rawY !== null && rawY !== undefined && rawY !== '' && Number.isFinite(Number(rawY)) ? Number(rawY) : null;
  return { x: numberOf(rowScalar(row, 'x_pos')), y, z: numberOf(rowScalar(row, 'z_pos')) };
}

function catalogDefinition(encounterArr: Row | undefined, id: string): Row | null {
  if (!encounterArr || !id) return null;
  const catalog = recordOf(encounterArr.catalog);
  return recordOf(catalog?.[id] ?? catalog?.[id.toUpperCase()] ?? catalog?.[id.toLowerCase()]);
}

/** PHP layer_value bridges used by perception, with the encounter catalog for thinned instances. */
export function ruleLayerValue(row: Row | null, key: string, encounterArr?: Row, visited = new Set<string>()): unknown {
  if (!row) return undefined;
  if (['blindsight', 'fiendsight', 'ghostsight', 'nightsight', 'spellsight', 'tremorsense', 'truesight', 'windsense'].includes(key)) {
    return senseValue(row, key);
  }
  const modifier = recordOf(recordOf(row.computed)?.creature)?.modifiers;
  const modifierRow = recordOf(modifier);
  if (modifierRow && modifierRow[key] !== null && modifierRow[key] !== undefined) return scalar(modifierRow[key]);
  const ownSlot = recordOf(row.item_layer_types)?.[key] ?? row[key];
  const ownValue = layerValue<unknown>(ownSlot);
  if (ownValue !== undefined && ownValue !== null) return ownValue;
  // A data-URL/Node import has no ambient window cache. Resolve the same catalogized provider
  // UUIDs through encounterArr.catalog explicitly before falling back to array-types' browser walk.
  for (const provider of ['creature_template', 'creature_sub_type', 'creature_type', 'weapon_classification',
    'weapon_ammo_classification', 'armor_classification', 'instrument_classification', 'armor_type']) {
    const slot = recordOf(row.item_layer_types)?.[provider];
    const entries = Array.isArray(slot) ? slot : slot === null || slot === undefined ? [] : [slot];
    for (const entry of entries) {
      const id = itemIdOf(entry);
      if (!id || visited.has(id)) continue;
      visited.add(id);
      const definition = typeof entry === 'object' && entry !== null ? recordOf(entry) : catalogDefinition(encounterArr, id);
      const value = ruleLayerValue(definition, key, encounterArr, visited);
      if (value !== undefined && value !== null) return value;
    }
  }
  // The dumped/browser encounter keeps catalogized carrier definitions on the encounter root.
  // array-types can resolve those references when the riding catalog is present on the row.
  const source = encounterArr?.catalog && !row.catalog ? { ...row, catalog: encounterArr.catalog } : row;
  const direct = layerOf<unknown>(source, key);
  if (direct !== undefined && direct !== null) return direct;
  const templateId = stringOf(recordOf(row.inventory)?.inventory_item_id ?? row.inventory_item_id).trim();
  if (templateId) {
    const definition = catalogDefinition(encounterArr, templateId);
    if (definition) return ruleLayerValue(definition, key, encounterArr, visited);
  }
  return undefined;
}

function layerHas(row: Row | null, key: string, encounterArr?: Row): boolean {
  return ruleLayerValue(row, key, encounterArr) !== undefined && ruleLayerValue(row, key, encounterArr) !== null;
}

export function itemIdOf(value: unknown): string {
  if (typeof value === 'string') return value.toUpperCase();
  const row = recordOf(value);
  return String(itemsCol(row, 'item_id') ?? row?.item_id ?? '').toUpperCase();
}

function statusPaused(status: Row): boolean {
  return statusIsPaused(status) || numberOf(status.status_campaign_paused ?? status.status_paused) === 1;
}

export function activeStatuses(row: unknown): Row[] {
  const holder = recordOf(row);
  const statuses = recordOf(holder?.statuses);
  const direct = Array.isArray(statuses?.target) ? statuses.target : Array.isArray(holder?.status_effects) ? holder.status_effects : [];
  return rowsOf(direct).filter(status => !statusPaused(status));
}

/** The canonical Burrowed status (MV1, owner-sanctioned 2026-09-27). */
export const BURROWED_STATUS_ID = '6436D343-5BCA-4147-8915-47A7C648977E';
/**
 * Owner ruling 6 (plans/burrow-and-blind-teleport-solid-rules-2026-09-24.md): a Burrowed creature's POSITION is public — every viewer's
 * map keeps its creature circle ("we know it is there, it is just obscured") — but it is targetable by tremorsense only. Twin of
 * ft3d_minimap_position_known() (ft3d_minimap_projection.php). A cover / targeting rule, never the hidden machinery.
 */
export function positionKnown(target: Row): boolean {
  return hasStatus(target, BURROWED_STATUS_ID);
}
/** The tremorsense exception (ruling 6): a viewer whose compiled tremorsense reaches the Burrowed target's centre (3D). Twin of
 *  ft3d_minimap_tremorsense_reaches(). */
export function tremorsenseReaches(viewer: Row, target: Row, senses: SensesSnapshot = sensesSnapshot(viewer)): boolean {
  const a = position(viewer), b = position(target);
  return senses.tremorsense > 0 && Math.hypot(a.x - b.x, (a.y ?? 0) - (b.y ?? 0), a.z - b.z) <= senses.tremorsense;
}
export function hasStatus(row: unknown, ...ids: string[]): boolean {
  const wanted = new Set(ids.map(id => id.toUpperCase()));
  return activeStatuses(row).some(status => wanted.has(String(itemsCol(status, 'item_id') ?? status.status_item_id ?? '').toUpperCase()));
}
/** Stealth's stance readers also honor passive grants; ordinary status callers retain their row semantics. */
export function hasStealthStatus(row: unknown, ...ids: string[]): boolean {
  return hasStatus(row, ...ids) || permanentStatusIds(row).some(id => ids.some(wanted => wanted.toUpperCase() === id.toUpperCase()));
}
/** Use the page's canonical reader, including compiled ability buckets and structured layer entries. */
export function permanentStatusIds(row: unknown): string[] {
  const pageRead = compileAmbient().ftStatusEffectPermanentUuids;
  if (typeof pageRead === 'function') return pageRead(row);
  if (!hasCompileAmbient()) {
    try {
      const read = (compileAmbient().parent as unknown as { ftStatusEffectPermanentUuids?: (r: unknown) => string[] })?.ftStatusEffectPermanentUuids;
      if (read) return read(row);
    } catch { /* Standalone catalog views use their pure row readers. */ }
  }
  return layerEntriesOf(row, 'status_effect_permanent').map(layerListEntryUuid);
}

export function statusDefinition(status: Row, encounterArr?: Row): Row | null {
  const embedded = recordOf(status.status_arr);
  if (embedded) return embedded;
  const id = String(itemsCol(status, 'item_id') ?? status.status_item_id ?? '').trim();
  return catalogDefinition(encounterArr, id);
}

export function statusField(status: Row, key: string): unknown {
  const pos = recordOf(status.position);
  return scalar(status[key] ?? pos?.[key]);
}

export function sizeWidthFt(row: Row): number {
  const sizeId = String(sizeIdOf(row) ?? '').toUpperCase();
  return SIZE_WIDTHS_FT[sizeId] ?? 5;
}

export function floorAt(shape: PerceptionShape, x: number, y: number): number {
  const boxes = shape.boxes.filter(box => x >= box.x.min - 1e-6 && x <= box.x.max + 1e-6
    && y >= box.y.min - 1e-6 && y <= box.y.max + 1e-6);
  const structural = boxes.filter(box => {
    const creation = recordOf(box.creation) ?? {};
    return box.navigationOpenGround !== true && creation.enclosure !== 'none'
      && creation.environment !== 'exterior-open' && creation.presetId !== 'exterior-open';
  });
  return boxes.length > 0 ? Math.min(...(structural.length > 0 ? structural : boxes).map(box => box.floor)) : 0;
}

export function floorUnder(shape: PerceptionShape, x: number, y: number, z: number): number {
  let lowest = Infinity, below = -Infinity;
  for (const box of shape.boxes) {
    if (x < box.x.min - 1e-6 || x > box.x.max + 1e-6 || y < box.y.min - 1e-6 || y > box.y.max + 1e-6) continue;
    lowest = Math.min(lowest, box.floor);
    if (box.floor <= z + 1e-6) below = Math.max(below, box.floor);
  }
  return below !== -Infinity ? below : lowest !== Infinity ? lowest : 0;
}

export function ceilingAt(shape: PerceptionShape, x: number, y: number): number | null {
  const boxes = shape.boxes.filter(box => x >= box.x.min - 1e-6 && x <= box.x.max + 1e-6
    && y >= box.y.min - 1e-6 && y <= box.y.max + 1e-6);
  const structural = boxes.filter(box => {
    const creation = recordOf(box.creation) ?? {};
    return box.navigationOpenGround !== true && creation.enclosure !== 'none'
      && creation.environment !== 'exterior-open' && creation.presetId !== 'exterior-open';
  });
  return boxes.length > 0 ? Math.max(...(structural.length > 0 ? structural : boxes).map(box => box.ceiling)) : null;
}

export function sightRayZ(shape: PerceptionShape, row: Row): number {
  const at = position(row);
  const y = at.y ?? 0;
  const floor = floorUnder(shape, at.x, y, at.z);
  // A supporting deck/foundation's ceiling is below the observer. Only a volume
  // containing the persisted feet can limit headroom; ceiling-only contact is excluded.
  let ceiling: number | null = null;
  for (const box of shape.boxes) {
    if (at.x < box.x.min || at.x > box.x.max || y < box.y.min || y > box.y.max
      || at.z < box.floor || at.z >= box.ceiling) continue;
    if (box.navigationOpenGround === true || box.creation?.enclosure === 'none'
      || boxEnvironment(box, shape) === 'exterior-open') continue;
    // Only exact ceiling contact is support from below. Feet just under a real
    // slab must still constrain the ray, including the final 0.01 ft of headroom.
    ceiling = Math.max(ceiling ?? -Infinity, box.ceiling);
  }
  let z = Math.max(floor, at.z) + sizeWidthFt(row) / 2;
  if (ceiling !== null && z > ceiling - 1e-6) z = ceiling - 1e-5;
  return z;
}

export function wallBlocks(shape: PerceptionShape, from: PerceptionPosition, to: PerceptionPosition, rayZ: number): boolean {
  if (from.y === null || to.y === null || shape.boxes.length === 0
    || !insideUnion(shape, from.x, from.y) || !insideUnion(shape, to.x, to.y)) return false;
  return segmentHit(shape, from.x, from.y, to.x, to.y, { z: rayZ, mode: 'sight', containToUnion: true }) !== null;
}

/**
 * The UNAUTHORED exterior fallback footprint a legacy (no stored shape) encounter projects from: its edge
 * is the old 100 ft range, not a wall — the scene draws none and the party walks past it onto the streamed
 * outdoor board — so creature sight and sound never stop there (2026-09-24). The marker is provenance;
 * the structure is re-proved so a marker carried into an edited shape goes inert. PHP twin:
 * ft_shape_is_derived_exterior_fallback().
 */
export function shapeIsDerivedExteriorFallback(shape: PerceptionShape): boolean {
  const sceneWalls = shape.sceneWalls;
  // PHP `!empty()` twin: any present, non-empty sceneWalls value disqualifies (not only a non-empty array).
  if (shape.derivedExteriorFallback !== true || shape.boxes.length !== 1 || (shape.openings?.length ?? 0) > 0
    || (!!sceneWalls && !(Array.isArray(sceneWalls) && sceneWalls.length === 0))) return false;
  const box = shape.boxes[0] as unknown as Record<string, unknown>;
  return !('creation' in box) && !('interior' in box);
}

// Creature sight. Unlike wallBlocks() (the AOE/light rules gate, whose void guard exists for
// un-authored anchors), an endpoint outside the union is judged: the ground around an authored room
// is where the party stands, and waving those rays through showed creatures through room walls
// (2026-09-15). Both endpoints inside keeps containToUnion; otherwise an open doorway out of the
// union admits the ray. A missing y is y = 0: build_pos_arr omits zero axes, so reading it as "no depth"
// let every creature on the y = 0 line see through walls. PHP twin: ft_shape_sight_blocks_line() +
// the normalization in ft3d_minimap_sensed_by_viewer().
export function sightWallBlocks(shape: PerceptionShape, from: PerceptionPosition, to: PerceptionPosition, rayZ: number): boolean {
  if (shape.boxes.length === 0 || shapeIsDerivedExteriorFallback(shape)) return false;
  const fromY = from.y ?? 0; const toY = to.y ?? 0;
  const bothInside = sightRayContainedToUnion(shape, from, to);
  return segmentHit(shape, from.x, fromY, to.x, toY, { z: rayZ, mode: 'sight', containToUnion: bothInside }) !== null;
}

function sightRayContainedToUnion(shape: PerceptionShape, from: PerceptionPosition, to: PerceptionPosition): boolean {
  // Authored open decks and islands share the outdoor space between their footprints.
  // Actual wall faces still occlude; an unmodelled patch of sea is not a wall.
  return sightEndpointContainedToUnion(shape, from) && sightEndpointContainedToUnion(shape, to);
}

function sightEndpointContainedToUnion(shape: PerceptionShape, at: PerceptionPosition): boolean {
  let inside = false, exterior = false;
  const y = at.y ?? 0;
  for (const box of shape.boxes) {
    // insideUnion() builds a containing-box array. Keep its footprint tolerance
    // here without that per-ray allocation, then classify physical residency.
    if (at.x >= box.x.min - 1e-6 && at.x <= box.x.max + 1e-6
      && y >= box.y.min - 1e-6 && y <= box.y.max + 1e-6) inside = true;
    if (at.x < box.x.min || at.x > box.x.max || y < box.y.min || y > box.y.max
      || at.z < box.floor - 0.01 || at.z >= box.ceiling - 1e-6) continue;
    // An outdoor footprint cannot reclassify a room inside it as open air.
    if (boxEnvironment(box, shape) !== 'exterior-open') return true;
    exterior = true;
  }
  return inside && !exterior;
}

// see_through_walls_max_thickness_ft (2026-10-07, Warlock Patron of the Void L30 "Voidsight": "You can see through walls or solid
// objects up to 5 feet thick"). SIGHT ONLY — light, fog, sound, AOE and line of effect keep their own gates. Exact intervals along the
// floor-plane ray (feet from the viewer): SOLID = every scene-wall slab the ray passes through (path length, its openings cut out) plus
// every gap BETWEEN two stretches of the room union (rock between rooms; a leading / trailing stretch outside every room is open air),
// merged; each blocking face crossing (segmentHit, keep-tracing) that is not the edge of a solid interval — a zero-thickness room face or
// a closed door — adds one wall run (WALL_RUN_THICKNESS_FT 0.5). Cumulative along the ray; a walk that exhausts its guard is BLOCKED.
// PHP twin: ft_shape_sight_wall_thickness_ft() / ft_shape_sight_blocks_line_for_viewer() in ft3d_shape_core.php — change them together.
export const SEE_THROUGH_WALLS_LAYER = 'see_through_walls_max_thickness_ft';
const SEE_THROUGH_WALL_NOMINAL_FT = 0.5;
const SEE_THROUGH_EDGE_FT = 0.02;
type SightInterval = [number, number];
type SceneWallLike = { axis?: unknown; at?: unknown; thicknessFt?: unknown; span?: { min?: unknown; max?: unknown };
  z?: { min?: unknown; max?: unknown }; openings?: Array<{ span?: { min?: unknown; max?: unknown }; z?: { min?: unknown; max?: unknown } } | null> };
/** The d ∈ [0, len] where c0 + u·d lies within [lo, hi] (null = never). */
function sightRayRange(c0: number, u: number, lo: number, hi: number, len: number): SightInterval | null {
  let a: number, b: number;
  if (Math.abs(u) < 1e-12) { if (c0 < lo || c0 > hi) return null; a = 0; b = len; }
  else { a = (lo - c0) / u; b = (hi - c0) / u; if (a > b) { const t = a; a = b; b = t; } }
  a = Math.max(a, 0); b = Math.min(b, len);
  return b > a ? [a, b] : null;
}
function sightIntersect(p: SightInterval | null, q: SightInterval | null): SightInterval | null {
  if (!p || !q) return null;
  const a = Math.max(p[0], q[0]), b = Math.min(p[1], q[1]);
  return b > a ? [a, b] : null;
}
function sightMerge(list: SightInterval[]): SightInterval[] {
  const sorted = [...list].sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  const out: SightInterval[] = [];
  for (const iv of sorted) {
    const last = out[out.length - 1];
    if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]); else out.push([iv[0], iv[1]]);
  }
  return out;
}
/** Feet of wall the floor-plane sight ray from → to crosses (Infinity once a walk cannot finish). */
export function sightWallThicknessFt(shape: PerceptionShape, from: PerceptionPosition, to: PerceptionPosition, rayZ: number, limitFt: number): number {
  if (shape.boxes.length === 0 || shapeIsDerivedExteriorFallback(shape)) return 0;
  const x1 = from.x, y1 = from.y ?? 0, x2 = to.x, y2 = to.y ?? 0;
  const length = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1)); // PHP sqrt twin (not Math.hypot)
  if (length < 1e-6) return 0;
  const ux = (x2 - x1) / length, uy = (y2 - y1) / length;
  const solid: SightInterval[] = [];
  for (const wall of ((shape as unknown as { sceneWalls?: unknown }).sceneWalls ?? []) as SceneWallLike[]) {
    if (!wall || (wall.axis !== 'x' && wall.axis !== 'y')) continue;
    const at = Number(wall.at ?? 0), half = Math.max(0.05, Number(wall.thicknessFt ?? 0.5)) / 2;
    const min = Number(wall.span?.min ?? 0), max = Number(wall.span?.max ?? 0);
    const floor = Number(wall.z?.min ?? 0), ceiling = Number(wall.z?.max ?? 0);
    if (!(max > min) || !(ceiling > floor) || rayZ < floor || rayZ > ceiling) continue;
    const [ac0, au, al0, alu] = wall.axis === 'x' ? [x1, ux, y1, uy] : [y1, uy, x1, ux];
    const body = sightIntersect(sightRayRange(ac0, au, at - half, at + half, length), sightRayRange(al0, alu, min, max, length));
    if (!body) continue;
    let pieces: SightInterval[] = [body];
    for (const o of wall.openings ?? []) {
      if (!o || rayZ < Number(o.z?.min ?? floor) || rayZ > Number(o.z?.max ?? ceiling)) continue;
      const hole = sightRayRange(al0, alu, Number(o.span?.min ?? 0), Number(o.span?.max ?? 0), length);
      if (!hole) continue;
      pieces = pieces.flatMap((p): SightInterval[] => {
        const cut = sightIntersect(p, hole);
        if (!cut) return [p];
        return ([[p[0], cut[0]], [cut[1], p[1]]] as SightInterval[]).filter(r => r[1] > r[0]);
      });
    }
    solid.push(...pieces);
  }
  const union = sightMerge(shape.boxes.flatMap((box): SightInterval[] => {
    const iv = sightIntersect(sightRayRange(x1, ux, box.x.min, box.x.max, length), sightRayRange(y1, uy, box.y.min, box.y.max, length));
    return iv ? [iv] : [];
  }));
  for (let i = 1; i < union.length; i++) solid.push([union[i - 1]![1], union[i]![0]]);
  const merged = sightMerge(solid);
  let total = merged.reduce((sum, iv) => sum + (iv[1] - iv[0]), 0);
  if (total > limitFt) return total;
  const bothInside = sightRayContainedToUnion(shape, from, to);
  let start = 0;
  for (let guard = 0; guard < 256; guard++) {
    const hit = segmentHit(shape, x1 + ux * start, y1 + uy * start, x2, y2, { z: rayZ, mode: 'sight', containToUnion: bothInside });
    if (!hit) return total;
    const d = start + hit.t * (length - start);
    if (!merged.some(iv => d >= iv[0] - SEE_THROUGH_EDGE_FT && d <= iv[1] + SEE_THROUGH_EDGE_FT)) {
      total += SEE_THROUGH_WALL_NOMINAL_FT;
      if (total > limitFt) return total;
    }
    start = d + 0.01;
    if (start >= length) return total;
  }
  return Infinity;
}
/** The viewer's see-through-walls limit: the finalize-promoted ROOT value (a PC's passive) or its own layer — highest wins. */
export function seeThroughWallsFt(viewer: Row | null, encounterArr?: Row): number {
  if (!viewer) return 0;
  const root = Number(scalar(viewer[SEE_THROUGH_WALLS_LAYER] as unknown) ?? 0);
  const layered = Number(ruleLayerValue(viewer, SEE_THROUGH_WALLS_LAYER, encounterArr) ?? 0);
  return Math.max(0, Number.isFinite(root) ? Math.trunc(root) : 0, Number.isFinite(layered) ? Math.trunc(layered) : 0);
}
/** sightWallBlocks() for a specific viewer: a Voidsight holder sees through walls within its limit. */
export function sightWallBlocksForViewer(shape: PerceptionShape, viewer: Row | null, from: PerceptionPosition, to: PerceptionPosition,
  rayZ: number, encounterArr?: Row): boolean {
  if (!sightWallBlocks(shape, from, to, rayZ)) return false;
  const limit = seeThroughWallsFt(viewer, encounterArr);
  return limit <= 0 || sightWallThicknessFt(shape, from, to, rayZ, limit) > limit;
}

export function boxAt(shape: PerceptionShape, at: PerceptionPosition): PerceptionBox | null {
  if (at.y === null) return null;
  return shape.boxes.find(box => at.x >= box.x.min && at.x <= box.x.max && at.y! >= box.y.min && at.y! <= box.y.max
    && at.z >= box.floor - 0.01 && at.z <= box.ceiling + 0.01) ?? null;
}

export function roomIdsAt(shape: PerceptionShape, at: PerceptionPosition): string[] {
  const singleBox = shape.boxes.length === 1;
  const ids: string[] = [];
  for (const box of shape.boxes) {
    const inDepth = at.y === null ? singleBox : at.y >= box.y.min && at.y <= box.y.max;
    if (at.x >= box.x.min && at.x <= box.x.max && inDepth && at.z >= box.floor - 0.01 && at.z <= box.ceiling + 0.01) {
      const id = String(box.id ?? '').trim();
      if (id && !ids.includes(id)) ids.push(id);
    }
  }
  return ids;
}

export function visibilityCells(box: PerceptionBox, cellSize = 5): VisibilityCell[] {
  const roomId = String(box.id ?? '').trim();
  if (!roomId || box.x.max <= box.x.min || box.y.max <= box.y.min || cellSize <= 0) return [];
  const cells: VisibilityCell[] = [];
  let column = 0;
  for (let x = box.x.min; x < box.x.max - 0.0001; x += cellSize, column += 1) {
    let row = 0;
    for (let y = box.y.min; y < box.y.max - 0.0001; y += cellSize, row += 1) {
      cells.push({ id: `${roomId}:${column}:${row}`, roomId, x: { min: x, max: Math.min(box.x.max, x + cellSize) },
        y: { min: y, max: Math.min(box.y.max, y + cellSize) }, floor: box.floor, ceiling: box.ceiling });
    }
  }
  return cells;
}

function preparePerceptionCache(cache: PerceptionCache | undefined, physicalEpoch: string): void {
  if (!cache || !physicalEpoch || cache.physicalEpoch === physicalEpoch) return;
  cache.physicalEpoch = physicalEpoch;
  cache.lightField = null;
}

function preparePerceptionGeometry(cache: PerceptionCache | undefined, shape: PerceptionShape): string {
  const key = JSON.stringify(shape);
  if (cache && cache.geometryKey !== key) {
    cache.geometryKey = key;
    cache.lightField = null;
    const roomKeys = new Map(shape.boxes.map(box => [String(box.id ?? '').trim(),
      JSON.stringify([box.x, box.y, box.floor, box.ceiling])]));
    for (const [roomId, previous] of cache.cellGeometryByRoom) if (roomKeys.get(roomId) !== previous) {
      cache.visibilityCellsByRoom.delete(roomId);
    }
    cache.cellGeometryByRoom = roomKeys;
    cache.boxAtByCellId.clear();
    cache.teamPreviews.clear();
    cache.explorationRegistry = null;
  }
  return key;
}

function cachedVisibilityCells(
  box: PerceptionBox,
  cache?: PerceptionCache,
  physicalEpoch = '',
): VisibilityCell[] {
  if (!cache || !physicalEpoch) return visibilityCells(box);
  preparePerceptionCache(cache, physicalEpoch);
  const roomId = String(box.id ?? '').trim();
  const held = cache.visibilityCellsByRoom.get(roomId);
  if (held) return held;
  const cells = visibilityCells(box);
  cache.visibilityCellsByRoom.set(roomId, cells);
  return cells;
}

export function boxStructuralEnvironment(box: PerceptionBox, creation: Row): string | null {
  const explicit = box.environment ?? creation.environment;
  const preset = String(box.presetId ?? creation.presetId ?? '').trim().toLowerCase();
  if (preset === 'exterior-open') return 'exterior-open';
  if (preset && preset !== 'legacy-structural') return 'interior';
  return explicit === 'exterior-open' || explicit === 'interior' ? explicit : null;
}

export function boxCoveredFromAbove(box: PerceptionBox, shape: PerceptionShape): boolean {
  const id = String(box.id ?? '').trim();
  return shape.boxes.some(other => String(other.id ?? '').trim() !== id
    && Math.min(box.x.max, other.x.max) - Math.max(box.x.min, other.x.min) > 0.01
    && Math.min(box.y.max, other.y.max) - Math.max(box.y.min, other.y.min) > 0.01
    && other.floor >= box.ceiling - 0.01);
}

export function boxEnvironment(box: PerceptionBox, shape?: PerceptionShape): string | null {
  const creation = recordOf(box.creation);
  if (!creation) return null;
  const structural = boxStructuralEnvironment(box, creation);
  if (structural === 'interior' && creation.roofRemoved === true && (!shape || !boxCoveredFromAbove(box, shape))) return 'exterior-open';
  return structural;
}

export function windowDaylight(opening: PerceptionOpening, room: PerceptionBox, campaignLight: string, underground: boolean): string | null {
  const intent = recordOf(recordOf(opening.creation)?.connectionIntent) ?? {};
  const kind = String(intent.kind ?? opening.kind ?? '').trim().toLowerCase();
  // DAFT3D S26: exteriority is the compiler's `creation.facing` stamp (the S11 generator's windows are
  // 'window-intent'); the legacy explicit `exterior` flag still reads, and an interior stamp wins.
  const facing = recordOf(opening.creation)?.facing;
  const exterior = facing === 'exterior' || (facing !== 'interior' && (opening.exterior === true || intent.exterior === true));
  const roomPreset = String(recordOf(room.creation)?.presetId ?? '').trim().toLowerCase();
  if ((kind !== 'window' && kind !== 'window-intent') || !exterior || opening.transparent === false || underground || roomPreset === 'wet-cave-interior') return null;
  return campaignLight === PERCEPTION_LIGHT.bright || campaignLight === PERCEPTION_LIGHT.dim ? PERCEPTION_LIGHT.dim : null;
}

function knownLight(value: unknown): string | null {
  const light = typeof value === 'string' ? value : '';
  return (Object.values(PERCEPTION_LIGHT) as string[]).includes(light) ? light : null;
}

export function lightContext(encounterArr: Row, shape: PerceptionShape,
  seed?: { override?: string | null; effective?: string; underground?: boolean }): PerceptionLightContext {
  const override = knownLight(encounterField(encounterArr, 'encounter_light'));
  // A seeded null is authoritative Automatic light, not a missing seed. `?? override` would
  // silently resurrect a stale row override and diverge from the server context receipt.
  const finalOverride = seed && Object.prototype.hasOwnProperty.call(seed, 'override')
    ? knownLight(seed.override)
    : override;
  const effective = finalOverride ?? knownLight(seed?.effective) ?? knownLight(encounterField(encounterArr, 'campaign_light')) ?? PERCEPTION_LIGHT.pitch;
  const environment = String(encounterField(encounterArr, 'encounter_environment') ?? '').toUpperCase();
  const underground = seed?.underground ?? environmentIsUnderground(environment);
  const boxesById = new Map<string, PerceptionBox>();
  let authored = false;
  for (const box of shape.boxes) {
    const id = String(box.id ?? '').trim();
    if (!id) continue;
    boxesById.set(id, box);
    authored ||= boxEnvironment(box, shape) !== null;
  }
  const windows: PerceptionLightContext['windows'] = [];
  if (finalOverride === null && !underground) {
    for (const opening of shape.openings ?? []) {
      const axis = opening.plane?.axis;
      if (!axis) continue;
      const at = numberOf(opening.plane?.at);
      const spanMid = (numberOf(opening.span?.min) + numberOf(opening.span?.max)) / 2;
      for (const [boxId, box] of boxesById) {
        if (boxEnvironment(box, shape) !== 'interior') continue;
        const inward = Math.min(0.5, (box.x.max - box.x.min) / 4, (box.y.max - box.y.min) / 4);
        if (!(inward > 0)) continue;
        let sample: { x: number; y: number } | null = null;
        if (axis === 'x' && spanMid >= box.y.min && spanMid <= box.y.max
          && (Math.abs(at - box.x.min) < 0.01 || Math.abs(at - box.x.max) < 0.01)) {
          sample = { x: Math.abs(at - box.x.min) < 0.01 ? box.x.min + inward : box.x.max - inward, y: spanMid };
        } else if (axis === 'y' && spanMid >= box.x.min && spanMid <= box.x.max
          && (Math.abs(at - box.y.min) < 0.01 || Math.abs(at - box.y.max) < 0.01)) {
          sample = { x: spanMid, y: Math.abs(at - box.y.min) < 0.01 ? box.y.min + inward : box.y.max - inward };
        }
        if (!sample) continue;
        const daylight = windowDaylight(opening, box, effective, underground);
        const inside = sample.x > box.x.min && sample.x < box.x.max && sample.y > box.y.min && sample.y < box.y.max
          && insideUnion(shape, sample.x, sample.y);
        if (daylight && inside) windows.push({ ...sample, boxId, light: daylight });
      }
    }
  }
  return { override: finalOverride, effective, underground, windows, authored,
    opaquePaintedWalls: paintedWallOpaqueGeometries(activeStatuses(encounterArr)) };
}

export function itemAppliesObscuredStatus(definition: Row | null): boolean {
  return !!definition && layerEntriesOf(definition, 'status_effect_applied_to_target_after_contesting_roll_failed')
    .some(entry => itemIdOf(entry) === PERCEPTION_STATUS.obscured);
}

// Only facts read by source construction: an unlit carrier's position/AP never enters this key.
function sourceFacts(encounterArr: Row): string {
  const carriers = [...rowsOf(encounterArr.encounter_creatures_arr), ...rowsOf(encounterArr.encounter_inventories_arr)];
  const facts: unknown[] = [];
  for (const row of carriers) {
    const radius = Math.abs(numberOf(ruleLayerValue(row, 'bright_light_dim_light', encounterArr)));
    if (radius > 0) facts.push([entityId(row, 'creature'), entityId(row, 'inventory'), radius,
      posCoord(row, 'x_pos') ?? 0, posCoord(row, 'y_pos') ?? 0, posCoord(row, 'z_pos') ?? 0,
      ruleLayerValue(row, 'magical_light', encounterArr) ?? 0, ruleLayerValue(row, 'sunlight_light', encounterArr) ?? 0]);
  }
  const entries = [...activeStatuses(encounterArr).map(status => ({ status, row: null as Row | null })),
    ...carriers.flatMap(row => activeStatuses(row).map(status => ({ status, row })))];
  for (const { status, row } of entries) {
    const stamped = numberOf(statusField(status, 'status_aoe_source')) === 1;
    const connected = String(statusField(status, 'status_connected_id') ?? '');
    if (stamped && connected) facts.push(['stamp', connected]);
    const definition = statusDefinition(status, encounterArr);
    if (!definition) continue;
    const light = Math.abs(numberOf(ruleLayerValue(definition, 'bright_light_dim_light', encounterArr)));
    const dark = truthyPhp(ruleLayerValue(definition, 'magical_darkness', encounterArr));
    const obscured = itemAppliesObscuredStatus(definition)
      && (layerHas(status, 'status_aoe_distance', encounterArr) || layerHas(status, 'status_distance_point_x', encounterArr));
    if (!(light > 0 && !row) && !dark && !obscured) continue;
    const follows = truthyPhp(ruleLayerValue(definition, 'aoe_follows_target', encounterArr));
    facts.push([!!row, stamped, connected, follows, follows && row ? position(row) : null,
      light, dark, obscured, ruleLayerValue(definition, 'magical_light', encounterArr) ?? 0,
      ruleLayerValue(definition, 'sunlight_light', encounterArr) ?? 0, ruleLayerValue(definition, 'aoe_distance', encounterArr),
      ...['status_origin_point_x', 'status_origin_point_y', 'status_origin_point_z', 'status_distance_point_x',
        'status_distance_point_y', 'status_aoe_distance'].map(key => statusField(status, key))]);
  }
  return JSON.stringify(facts);
}

function retainSources(previous: PerceptionSource[], next: PerceptionSource[]): PerceptionSource[] {
  const byValue = new Map(previous.map(source => [JSON.stringify(source), source]));
  const retained = next.map(source => byValue.get(JSON.stringify(source)) ?? source);
  return sameEntries(previous, retained) ? previous : retained;
}

export function collectLightAndDarkSources(encounterArr: Row, cache?: PerceptionCache): {
  lightSources: PerceptionSource[]; darkSources: PerceptionSource[]; obscuredSources: PerceptionSource[];
} {
  requireInventoryFloor(encounterArr);
  const key = cache ? sourceFacts(encounterArr) : '';
  if (cache?.sources?.key === key) return cache.sources.value;
  const lightSources: PerceptionSource[] = [];
  const darkSources: PerceptionSource[] = [];
  const obscuredSources: PerceptionSource[] = [];
  const creatures = rowsOf(encounterArr.encounter_creatures_arr);
  const inventories = rowsOf(encounterArr.encounter_inventories_arr);
  const pushEntityLight = (row: Row): void => {
    const radius = Math.abs(numberOf(ruleLayerValue(row, 'bright_light_dim_light', encounterArr)));
    if (!(radius > 0)) return;
    lightSources.push({ x: posCoord(row, 'x_pos') ?? 0, z: posCoord(row, 'y_pos') ?? 0,
      elevation: posCoord(row, 'z_pos') ?? 0, radius,
      magical_light: ruleLayerValue(row, 'magical_light', encounterArr) ?? 0,
      sunlight_light: ruleLayerValue(row, 'sunlight_light', encounterArr) ?? 0 });
  };
  creatures.forEach(pushEntityLight);
  inventories.forEach(pushEntityLight);

  type StatusEntry = { status: Row; attached: PerceptionPosition | null };
  const entries: StatusEntry[] = activeStatuses(encounterArr).map(status => ({ status, attached: null }));
  for (const carrier of [...creatures, ...inventories]) {
    for (const status of activeStatuses(carrier)) entries.push({ status, attached: position(carrier) });
  }
  const stampedConnected = new Set(entries.flatMap(({ status }) => numberOf(statusField(status, 'status_aoe_source')) === 1
    ? [String(statusField(status, 'status_connected_id') ?? '')].filter(Boolean) : []));
  for (const { status, attached } of entries) {
    const definition = statusDefinition(status, encounterArr);
    if (!definition) continue;
    const stamped = numberOf(statusField(status, 'status_aoe_source')) === 1;
    const follows = truthyPhp(ruleLayerValue(definition, 'aoe_follows_target', encounterArr));
    const connectedId = String(statusField(status, 'status_connected_id') ?? '');
    if (attached && !stamped && (!follows || (!!connectedId && stampedConnected.has(connectedId)))) continue;

    if (!attached) {
      const radius = Math.abs(numberOf(ruleLayerValue(definition, 'bright_light_dim_light', encounterArr)));
      if (radius > 0) lightSources.push({ x: numberOf(statusField(status, 'status_origin_point_x')),
        z: numberOf(statusField(status, 'status_origin_point_y')), elevation: numberOf(statusField(status, 'status_origin_point_z')),
        radius, magical_light: ruleLayerValue(definition, 'magical_light', encounterArr) ?? 0,
        sunlight_light: ruleLayerValue(definition, 'sunlight_light', encounterArr) ?? 0 });
    }

    const isAttached = attached !== null && attached.y !== null;
    const cx = follows && isAttached ? attached.x : numberOf(statusField(status, 'status_origin_point_x'));
    const cz = follows && isAttached ? attached.y! : numberOf(statusField(status, 'status_origin_point_y'));
    const elevation = follows && isAttached ? attached.z : numberOf(statusField(status, 'status_origin_point_z'));
    if (truthyPhp(ruleLayerValue(definition, 'magical_darkness', encounterArr))) {
      let radius = numberOf(statusField(status, 'status_aoe_distance'));
      if (!(radius > 0)) radius = numberOf(ruleLayerValue(definition, 'aoe_distance', encounterArr));
      radius = Math.abs(radius);
      if (radius > 0) darkSources.push({ x: cx, z: cz, elevation, radius });
    }
    if (itemAppliesObscuredStatus(definition)
      && (layerHas(status, 'status_aoe_distance', encounterArr) || layerHas(status, 'status_distance_point_x', encounterArr))) {
      let ox = cx;
      let oz = cz;
      if (!(follows && isAttached)) {
        const originX = numberOf(statusField(status, 'status_origin_point_x'));
        const originY = numberOf(statusField(status, 'status_origin_point_y'));
        ox = (originX + numberOf(statusField(status, 'status_distance_point_x'), originX)) / 2;
        oz = (originY + numberOf(statusField(status, 'status_distance_point_y'), originY)) / 2;
      }
      let radius = numberOf(statusField(status, 'status_aoe_distance'));
      if (!(radius > 0)) radius = numberOf(ruleLayerValue(definition, 'aoe_distance', encounterArr));
      radius = Math.abs(radius);
      if (radius > 0) obscuredSources.push({ x: ox, z: oz, elevation, radius });
    }
  }
  const previous = cache?.sources?.value;
  const value = previous ? { lightSources: retainSources(previous.lightSources, lightSources),
    darkSources: retainSources(previous.darkSources, darkSources), obscuredSources: retainSources(previous.obscuredSources, obscuredSources) }
    : { lightSources, darkSources, obscuredSources };
  if (cache) cache.sources = { key, value };
  return value;
}

/** Combat-island convenience twin: persistent obscuration in the canonical server source shape. */
export function collectObscuredSources(encounterArr: Row): PerceptionSource[] {
  return collectLightAndDarkSources(encounterArr).obscuredSources;
}

export function fixtureLightSources(shape: PerceptionShape, radius = 0, cache?: PerceptionCache): PerceptionSource[] {
  const key = cache ? JSON.stringify([cache.geometryKey, radius]) : '';
  if (cache?.fixtures?.key === key) return cache.fixtures.value;
  const value: PerceptionSource[] = radius > 0 && shape.boxes.length > 0
    ? shapeLightFixtures(shape).map(fixture => ({ x: fixture.x, z: fixture.y, elevation: fixture.z,
      radius, magical_light: 0, sunlight_light: 0, fixture: fixture.id })) : [];
  if (cache) cache.fixtures = { key, value };
  return value;
}

export function admittedObscuredSources(shape: PerceptionShape, target: Row | PerceptionPosition,
  sources: PerceptionSource[]): PerceptionSource[] {
  const at = 'x' in target && 'z' in target && !('position' in target) ? target as PerceptionPosition : position(target as Row);
  if (at.y === null || shape.boxes.length === 0) return sources;
  return sources.filter(source => !wallBlocks(shape,
    { x: source.x, y: source.z, z: numberOf(source.elevation, at.z) }, at,
    Math.min(numberOf(source.elevation, at.z), at.z) + 2.5));
}

export function maxLight(left: string, right: string): string {
  const rank: Record<string, number> = { [PERCEPTION_LIGHT.magicalDarkness]: 0, [PERCEPTION_LIGHT.pitch]: 1,
    [PERCEPTION_LIGHT.dim]: 2, [PERCEPTION_LIGHT.bright]: 3 };
  return (rank[left] ?? 0) > (rank[right] ?? 0) ? left : right;
}

export function calculateLightAt(x: number, z: number, baseLight: string, lightSources: PerceptionSource[],
  darkSources: PerceptionSource[], elevation: number | null = null): string {
  const distance = (source: PerceptionSource): number => Math.hypot(x - source.x, z - source.z,
    elevation !== null && source.elevation !== null && source.elevation !== undefined ? elevation - source.elevation : 0);
  const inDarkness = darkSources.some(source => distance(source) <= source.radius);
  let brightCount = 0;
  let dimCount = 0;
  let hasMagicalLight = false;
  let magicalBright = false;
  for (const source of lightSources) {
    const dist = distance(source);
    const radius = Math.abs(source.radius);
    if (dist <= radius) {
      brightCount += 1;
      if (truthyPhp(source.magical_light)) { hasMagicalLight = true; magicalBright = true; }
    } else if (dist <= radius * 2) {
      dimCount += 1;
      if (truthyPhp(source.magical_light)) hasMagicalLight = true;
    }
  }
  if (inDarkness) return magicalBright ? PERCEPTION_LIGHT.bright : PERCEPTION_LIGHT.magicalDarkness;
  let effective = brightCount > 0 ? PERCEPTION_LIGHT.bright
    : dimCount >= 2 ? maxLight(PERCEPTION_LIGHT.bright, baseLight)
      : dimCount > 0 ? maxLight(PERCEPTION_LIGHT.dim, baseLight) : baseLight;
  if (hasMagicalLight && baseLight === PERCEPTION_LIGHT.pitch && (dimCount > 0 || brightCount > 0)) {
    effective = maxLight(effective, PERCEPTION_LIGHT.dim);
  }
  return effective;
}

/** Twin of ft3d_minimap_base_light_for_box. Owner ruling 2026-09-03: a roof-removed room that another floor
 * covers is still enclosed (the light comes from outside), so the environment read is shape-aware here too. */
export function baseLightForBox(context: PerceptionLightContext, box: PerceptionBox | null, shape?: PerceptionShape): string {
  if (context.override !== null) return context.override;
  if (box && boxEnvironment(box, shape) === 'interior') return PERCEPTION_LIGHT.pitch;
  return context.effective;
}

export function lightAt(context: PerceptionLightContext, shape: PerceptionShape, at: PerceptionPosition,
  box: PerceptionBox | null, lightSources: PerceptionSource[], darkSources: PerceptionSource[]): string {
  // DAFT3D D-S11-13: resolve daylight before torches/spells, using the selected room and its
  // elevation band. The core's 2D lookup must never select a different floor at the same x/y.
  // An explicit GM override is the ambient base everywhere, including roofed rooms.
  // Apertures only derive indoor daylight in Automatic mode; treating an override as
  // outdoor daylight here turns forced Bright back into pitch black away from windows.
  const apertureAmbient = context.override === null && at.y !== null && box !== null && boxIsRoofed(box);
  // The room is PINNED (`inside`) and every box rides along: facing and the one-hop interior relay
  // (DAFT3D S26) need the neighbours, and the pin keeps a different floor at this x/y out of it.
  const ambient = apertureAmbient ? ambientLightAt({ boxes: shape.boxes, openings: (shape.openings ?? []).filter(opening =>
    opening.z && opening.z.max > box.floor && opening.z.min < box.ceiling) },
  context.override ?? context.effective, at.x, at.y!, { inside: box }) : baseLightForBox(context, box, shape);
  const light = calculateLightAt(at.x, at.y ?? at.z, ambient,
    admittedObscuredSources(shape, at, lightSources), admittedObscuredSources(shape, at, darkSources), at.y === null ? null : at.z);
  if (!apertureAmbient && light === PERCEPTION_LIGHT.pitch && box && context.windows.length > 0 && at.y !== null) {
    const boxId = String(box.id ?? '').trim();
    for (const window of context.windows) {
      if (window.boxId === boxId && !wallBlocks(shape, { x: window.x, y: window.y, z: at.z }, at, at.z + 2.5)) return window.light;
    }
  }
  return light;
}

export function lightTierCode(light: string): number {
  if (light === PERCEPTION_LIGHT.bright) return 4;
  if (light === PERCEPTION_LIGHT.dim) return 3;
  if (light === PERCEPTION_LIGHT.magicalDarkness) return 2;
  return 1;
}

function lightFromTier(tier: number): string {
  return tier === 4 ? PERCEPTION_LIGHT.bright : tier === 3 ? PERCEPTION_LIGHT.dim
    : tier === 2 ? PERCEPTION_LIGHT.magicalDarkness : PERCEPTION_LIGHT.pitch;
}

export function lightField(shape: PerceptionShape, context: PerceptionLightContext, lightSources: PerceptionSource[],
  darkSources: PerceptionSource[], cellSize = 5, cache?: PerceptionCache,
  physicalEpoch = ''): { floors: Record<string, PerceptionLightFloor> } {
  preparePerceptionCache(cache, physicalEpoch);
  if (cache && physicalEpoch && cache.lightField) return cache.lightField;
  type BuildingFloor = { floor: number; boxes: PerceptionBox[]; minX: number; minY: number; maxX: number; maxY: number };
  const building = new Map<string, BuildingFloor>();
  for (const box of shape.boxes) {
    if (!String(box.id ?? '').trim()) continue;
    const floor = Number(box.floor);
    const existing = [...building.entries()].find(([, candidate]) => Math.abs(candidate.floor - floor) <= 0.01);
    const key = existing?.[0] ?? `@${floor}`;
    const floorGroup = building.get(key) ?? { floor, boxes: [], minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    floorGroup.boxes.push(box);
    floorGroup.minX = Math.min(floorGroup.minX, box.x.min); floorGroup.minY = Math.min(floorGroup.minY, box.y.min);
    floorGroup.maxX = Math.max(floorGroup.maxX, box.x.max); floorGroup.maxY = Math.max(floorGroup.maxY, box.y.max);
    building.set(key, floorGroup);
  }
  const floors: Record<string, PerceptionLightFloor> = {};
  // PHP's floor projection emits the physical bands in ascending numeric order.
  // Keep the record insertion order identical so serialized light grids and
  // their epochs remain parity-stable when shape boxes arrive in mixed order.
  const orderedBuilding = [...building.entries()].sort(([, left], [, right]) => left.floor - right.floor);
  for (const [key, source] of orderedBuilding) {
    const originX = Math.floor(source.minX / cellSize) * cellSize;
    const originY = Math.floor(source.minY / cellSize) * cellSize;
    const columns = Math.max(1, Math.ceil((source.maxX - originX) / cellSize - 0.0001));
    const rows = Math.max(1, Math.ceil((source.maxY - originY) / cellSize - 0.0001));
    const tiers = new Array<number>(columns * rows).fill(0);
    const cellIndex: Record<string, number> = {};
    const cellTiers: Record<string, number> = {};
    for (const box of source.boxes) for (const cell of cellSize === 5
      ? cachedVisibilityCells(box, cache, physicalEpoch)
      : visibilityCells(box, cellSize)) {
      const cx = (cell.x.min + cell.x.max) / 2;
      const cy = (cell.y.min + cell.y.max) / 2;
      const column = Math.min(columns - 1, Math.max(0, Math.floor((cx - originX) / cellSize)));
      const row = Math.min(rows - 1, Math.max(0, Math.floor((cy - originY) / cellSize)));
      const index = row * columns + column;
      const tier = lightTierCode(lightAt(context, shape, { x: cx, y: cy, z: cell.floor }, box, lightSources, darkSources));
      tiers[index] = Math.max(tiers[index]!, tier); cellIndex[cell.id] = index; cellTiers[cell.id] = tier;
    }
    floors[key] = { floor: source.floor, originX, originY, columns, rows, tiers, cellIndex, cellTiers };
  }
  const result = { floors };
  if (cache && physicalEpoch) cache.lightField = result;
  return result;
}

/**
 * The reach-geometry size of a row — twin of ft_combat_range_object_size_ft(): the size tier's width, widened to a
 * swarm's current occupied band (ft_swarm_occupied_width_ft ↔ swarmOccupiedWidthFt). Undefined when the tier is unknown
 * (a sizeless target is a point; a sizeless viewer has no reach). 2026-09-24: tier-only here made the browser's
 * see-by-touch floor disagree with the server's for swarms.
 */
function reachSizeFt(row: Row): number | undefined {
  const tier = SIZE_WIDTHS_FT[String(sizeIdOf(row) ?? '').toUpperCase()];
  if (tier === undefined || !(tier > 0)) return tier;
  const creature = row as unknown as Parameters<typeof getSwarmTemplateForCreature>[0];
  const def = getSwarmTemplateForCreature(creature);
  return def ? Math.max(tier, swarmOccupiedWidthFt(tier, def, swarmWidthLanesForCreature(creature, def), swarmVRatio01(creature))) : tier;
}

export function withinReach(viewer: Row, target: Row): boolean {
  const possessId = String(rowScalar(target, 'possess_id') ?? '').trim().toUpperCase();
  if (entityId(target, 'inventory') && possessId && possessId === entityId(viewer, 'creature').toUpperCase()) return true;
  const sourceSizeFeet = reachSizeFt(viewer);
  if (sourceSizeFeet === undefined || !(sourceSizeFeet > 0)) return false;
  const targetSizeFeet = reachSizeFt(target);
  const a = position(viewer); const b = position(target);
  const distance = combatCenterDistanceFt({ x: a.x, y: a.y ?? 0, z: a.z }, { x: b.x, y: b.y ?? 0, z: b.z },
    { sourceSizeFeet, targetSizeFeet, depthAuthored: true });
  return distance <= (reachOf(viewer) ?? numberOf(ruleLayerValue(viewer, 'reach'), 5));
}

export function targetGrounded(shape: PerceptionShape, row: Row): boolean {
  const at = position(row);
  if (at.y === null) {
    const size = SIZE_WIDTHS_FT[String(sizeIdOf(row) ?? '').toUpperCase()] ?? 0;
    const picture = recordOf(primaryPictureOf(row));
    const width = numberOf(picture?.picture_width, 100);
    const height = numberOf(picture?.picture_height, 100);
    const aspect = width !== 0 ? height / width : 1;
    return at.z - aspect * size / 2 <= 0;
  }
  return at.z - floorUnder(shape, at.x, at.y, at.z) <= 0.5;
}

function floorAtRow(shape: PerceptionShape, row: Row): number {
  const at = position(row);
  return floorBandForPosition(shape.boxes, at.x, at.y ?? 0, at.z);
}

/** Existing combat-island physical-grid read, now sharing this module's position/floor rules. */
export function physicalLightAt(grid: Ft3dCellLightGrid | null | undefined, shape: PerceptionShape,
  target: Row, decoded: Map<string, Uint8Array>): string {
  if (!isFt3dCellLightGrid(grid)) return PERCEPTION_LIGHT.pitch;
  const at = position(target);
  const targetFloor = floorAtRow(shape, target);
  const floor = grid.floors.find(candidate => Math.abs(candidate.floor - targetFloor) < 0.01);
  if (!floor) return String(grid.baseLight?.effective || PERCEPTION_LIGHT.pitch);
  const key = `${floor.floor}|${floor.originX}|${floor.originY}`;
  let bytes = decoded.get(key);
  if (!bytes) {
    bytes = decodeCellLightFloor(floor as Ft3dCellLightFloor, 'physical');
    decoded.set(key, bytes);
  }
  const tier = cellLightTierAt(floor, bytes, at.x, at.y ?? 0, grid.cellSizeFt);
  if (tier === CELL_LIGHT_TIER.bright) return PERCEPTION_LIGHT.bright;
  if (tier === CELL_LIGHT_TIER.dim) return PERCEPTION_LIGHT.dim;
  if (tier === CELL_LIGHT_TIER.magicalDarkness) return PERCEPTION_LIGHT.magicalDarkness;
  if (tier === CELL_LIGHT_TIER.pitchBlack) return PERCEPTION_LIGHT.pitch;
  return String(grid.baseLight?.effective || PERCEPTION_LIGHT.pitch);
}

export function sensesSnapshot(viewer: Row, encounterArr?: Row): SensesSnapshot {
  return { lair: String(itemsCol(viewer, 'item_type') ?? encounterCreatureField(viewer.encounter_creatures, 'item_type') ?? '').toLowerCase() === 'lair',
    blindsight: senseValue(viewer, 'blindsight'), fiendsight: senseValue(viewer, 'fiendsight'),
    ghostsight: senseValue(viewer, 'ghostsight'), nightsight: senseValue(viewer, 'nightsight'),
    spellsight: senseValue(viewer, 'spellsight'), tremorsense: senseValue(viewer, 'tremorsense'),
    truesight: senseValue(viewer, 'truesight'), windsense: senseValue(viewer, 'windsense'), blindsightBlind: truthyPhp(ruleLayerValue(viewer, 'blindsight_blind', encounterArr)),
    blinded: hasStatus(viewer, PERCEPTION_STATUS.blinded) };
}

export interface CanSeeOptions {
  allowWithinReach?: boolean; senses?: SensesSnapshot; encounterArr?: Row;
  /** can_see_with_senses($ignore_hidden = true): the stealth reveal / Hide-entry check judges a hidden target as if visible. */
  ignoreHidden?: boolean;
}

export function canSeeWithSenses(viewer: Row, target: Row, light: string, distance: number,
  obscuredSources: PerceptionSource[] = [], grounded = false, options: CanSeeOptions = {}): boolean {
  const viewerSenses = options.senses ?? sensesSnapshot(viewer, options.encounterArr);
  if (viewerSenses.lair) return true;
  // `not_detected_by_special_senses` (Monk "Beyond Detection") zeroes blindsight, ghostsight, nightsight,
  // spellsight and tremorsense AGAINST THIS TARGET at every decision below — server twin in
  // can_see_with_senses.php + obscured_target_perceived_by_viewer().
  const senses = truthyPhp(ruleLayerValue(target, 'not_detected_by_special_senses', options.encounterArr))
    ? { ...viewerSenses, blindsight: 0, ghostsight: 0, nightsight: 0, spellsight: 0, tremorsense: 0 }
    : viewerSenses;
  const invisible = hasStatus(target, PERCEPTION_STATUS.invisible, PERCEPTION_STATUS.greaterInvisible);
  const ethereal = hasStatus(target, PERCEPTION_STATUS.ethereal);
  // Server twin: can_see_with_senses.php. `not_detected_by_truesight` zeroes truesight AGAINST THIS
  // TARGET at every decision below; `windsense` is tremorsense's airborne mirror (!grounded).
  const trueR = truthyPhp(ruleLayerValue(target, 'not_detected_by_truesight', options.encounterArr))
    ? 0 : senses.truesight;
  const airborne = !grounded;
  if (!options.ignoreHidden && hasStealthStatus(target, PERCEPTION_STATUS.hidden)) return false;
  const allowWithinReach = options.allowWithinReach ?? true;
  if (allowWithinReach && withinReach(viewer, target) && !invisible && !ethereal) return true;
  if (!invisible && !ethereal && hasStatus(target, PERCEPTION_STATUS.obscured)) {
    if (allowWithinReach && withinReach(viewer, target)) return true;
    return (senses.blindsight > 0 && distance <= senses.blindsight)
      || (senses.tremorsense > 0 && grounded && distance <= senses.tremorsense);
  }
  if (obscuredSources.length > 0) {
    const viewerAt = position(viewer); const targetAt = position(target);
    const inZone = obscuredSources.some(source => Math.hypot(viewerAt.x - source.x, (viewerAt.y ?? 0) - source.z) <= source.radius
      || Math.hypot(targetAt.x - source.x, (targetAt.y ?? 0) - source.z) <= source.radius);
    if (inZone) return (senses.blindsight > 0 && distance <= senses.blindsight)
      || (senses.tremorsense > 0 && grounded && distance <= senses.tremorsense)
      || (senses.windsense > 0 && airborne && distance <= senses.windsense);
  }
  const targetLight = Math.abs(numberOf(ruleLayerValue(target, 'bright_light_dim_light', options.encounterArr)));
  if (targetLight > 0 && distance <= targetLight * 2) return true;
  if (senses.blinded || senses.blindsightBlind) return (senses.blindsight > 0 && distance <= senses.blindsight)
    || (senses.tremorsense > 0 && grounded && distance <= senses.tremorsense)
    || (senses.windsense > 0 && airborne && distance <= senses.windsense);
  if (invisible) return (senses.blindsight > 0 && distance <= senses.blindsight)
    || (trueR > 0 && distance <= trueR) || (senses.ghostsight > 0 && distance <= senses.ghostsight);
  if (ethereal) return (trueR > 0 && distance <= trueR)
    || (senses.ghostsight > 0 && distance <= senses.ghostsight);
  const spellEffect = activeStatuses(target).some(status => {
    const definition = statusDefinition(status, options.encounterArr);
    return String(itemsCol(status, 'item_type') ?? itemsCol(definition, 'item_type') ?? '').toLowerCase() === 'spell';
  });
  if (spellEffect && senses.spellsight > 0 && distance <= senses.spellsight) return true;
  if (light === PERCEPTION_LIGHT.bright || light === PERCEPTION_LIGHT.dim) return true;
  if (light === PERCEPTION_LIGHT.pitch) return (senses.nightsight > 0 && distance <= senses.nightsight)
    || (senses.blindsight > 0 && distance <= senses.blindsight)
    || (senses.tremorsense > 0 && grounded && distance <= senses.tremorsense)
    || (senses.windsense > 0 && airborne && distance <= senses.windsense)
    || (senses.fiendsight > 0 && distance <= senses.fiendsight)
    || (trueR > 0 && distance <= trueR);
  return (senses.blindsight > 0 && distance <= senses.blindsight)
    || (senses.tremorsense > 0 && grounded && distance <= senses.tremorsense)
    || (senses.windsense > 0 && airborne && distance <= senses.windsense)
    || (senses.fiendsight > 0 && distance <= senses.fiendsight)
    || (trueR > 0 && distance <= trueR);
}

/**
 * Twin of can_see_point_with_senses() (light_and_visibility_helpers.php) on the FT3D floor plane ('y_pos'): may the
 * viewer AIM at this bare map point? Unlike a creature target, reach never reveals a point; only the viewer's own
 * footprint is known while aiming (a blinded caster may still center a burst on themselves). Perception callers disable
 * `ownFootprintKnown`: knowing where to aim does not reveal an effect there. `point.y` is the floor-plane
 * depth; `light` is the light at the point; `obscuredSources` are the fog sources already admitted to the point.
 */
export function canSeePointWithSenses(viewer: Row, point: { x: number; y: number }, light: string, distance: number,
  obscuredSources: PerceptionSource[] = [], senses: SensesSnapshot = sensesSnapshot(viewer), ownFootprintKnown = true): boolean {
  if (senses.lair) return true;
  const at = position(viewer);
  const viewerY = at.y ?? 0;
  // get_object_box(): the size tier's footprint centred on the row (x extent by depth extent).
  const half = sizeWidthFt(viewer) / 2;
  if (ownFootprintKnown && point.x >= at.x - half && point.x <= at.x + half && point.y >= viewerY - half && point.y <= viewerY + half) return true;
  const within = (radius: number): boolean => radius > 0 && distance <= radius;
  if (obscuredSources.length > 0 && obscuredSources.some(source => Math.hypot(at.x - source.x, viewerY - source.z) <= source.radius
    || Math.hypot(point.x - source.x, point.y - source.z) <= source.radius)) {
    return within(senses.blindsight) || within(senses.tremorsense);
  }
  if (senses.blinded || senses.blindsightBlind) return within(senses.blindsight) || within(senses.tremorsense);
  if (light === PERCEPTION_LIGHT.bright || light === PERCEPTION_LIGHT.dim) return true;
  if (light === PERCEPTION_LIGHT.magicalDarkness) {
    return within(senses.blindsight) || within(senses.tremorsense) || within(senses.fiendsight) || within(senses.truesight);
  }
  return within(senses.nightsight) || within(senses.blindsight) || within(senses.tremorsense)
    || within(senses.fiendsight) || within(senses.truesight);
}

export interface SensedByViewerOptions {
  context?: PerceptionLightContext;
  allowWithinReach?: boolean;
  senses?: SensesSnapshot;
  lightAtTarget?: string;
  viewerRayZ?: number | null;
}

/**
 * DAFT3D S24 — where a viewer SEES the row of a drawn scene wall: the point of its face nearest the viewer
 * (just outside the slab). The row sits on the wall's centre line, INSIDE the solid slab, so a sight line
 * to it always crossed the face first. Twin of ft3d_minimap_scene_wall_face_point(). Null for other rows.
 */
export function sceneWallFacePoint(shape: PerceptionShape, target: Row, viewerAt: PerceptionPosition): PerceptionPosition | null {
  const walls = (shape as unknown as { sceneWalls?: Array<{ id?: unknown; thicknessFt?: unknown }> }).sceneWalls;
  if (!Array.isArray(walls) || !walls.length) return null;
  let generation: unknown = rowScalar(target, 'inventory_generation');
  if (typeof generation === 'string') { try { generation = JSON.parse(generation); } catch { return null; } }
  const run = recordOf(recordOf(generation)?.wallRun);
  const span = recordOf(run?.span);
  if (!run || typeof run.sceneWallId !== 'string' || !span || (run.axis !== 'x' && run.axis !== 'y')) return null;
  const wall = walls.find(candidate => candidate?.id === run.sceneWallId);
  if (!wall) return null;
  const at = Number(run.at);
  const half = Math.max(.05, Number(wall.thicknessFt ?? .5)) / 2;
  const viewer = { x: viewerAt.x, y: viewerAt.y ?? 0 };
  const normal = run.axis === 'x' ? viewer.x : viewer.y;
  const along = Math.max(Number(span.min), Math.min(Number(span.max), run.axis === 'x' ? viewer.y : viewer.x));
  const across = at + (normal >= at ? 1 : -1) * (half + .05);
  const z = position(target).z;
  return run.axis === 'x' ? { x: across, y: along, z } : { x: along, y: across, z };
}

export function sensedByViewer(encounterArr: Row, shape: PerceptionShape, viewer: Row, target: Row,
  lightSources: PerceptionSource[], darkSources: PerceptionSource[], obscuredSources: PerceptionSource[],
  options: SensedByViewerOptions = {}): boolean {
  const viewerAt = position(viewer); const targetAt = sceneWallFacePoint(shape, target, viewerAt) ?? position(target);
  const distance = Math.hypot(viewerAt.x - targetAt.x, (viewerAt.y ?? 0) - (targetAt.y ?? 0), viewerAt.z - targetAt.z);
  const context = options.context ?? lightContext(encounterArr, shape);
  const obscured = [...admittedObscuredSources(shape, targetAt, obscuredSources)];
  if(paintedWallObscuresRay(context.opaquePaintedWalls??[],
    {x:viewerAt.x,y:viewerAt.y??0,z:options.viewerRayZ??sightRayZ(shape,viewer)},
    {x:targetAt.x,y:targetAt.y??0,z:sightRayZ(shape,target)})) {
    // Reuse obscuration's established special-sense/hidden/touch policy instead of bypassing it.
    obscured.push({x:targetAt.x,z:targetAt.y??0,radius:0});
  }
  const targetLight = options.lightAtTarget ?? lightAt(context, shape, targetAt, boxAt(shape, targetAt), lightSources, darkSources);
  if (!canSeeWithSenses(viewer, target, targetLight, distance, obscured,
    targetAt.y === null ? targetGrounded(shape, target) : targetAt.z - floorUnder(shape, targetAt.x, targetAt.y, targetAt.z) <= 0.5,
    { allowWithinReach: options.allowWithinReach ?? true, senses: options.senses, encounterArr })) return false;
  if (shape.boxes.length > 0
    && sightWallBlocksForViewer(shape, viewer, viewerAt, targetAt, options.viewerRayZ ?? sightRayZ(shape, viewer), encounterArr)) return false;
  return true;
}

function explorationVerticalDiscoveryAllowed(
  shape: PerceptionShape,
  viewer: Row,
  cell: VisibilityCell,
): boolean {
  const targetBox = shape.boxes.find(box => String(box.id ?? '').trim() === cell.roomId.trim()) ?? null;
  if (!targetBox || boxEnvironment(targetBox, shape) !== 'interior') return true;
  const viewerPosition = position(viewer);
  const floor = Number.isFinite(Number(cell.floor)) ? Number(cell.floor) : Number(targetBox.floor);
  const ceiling = Number.isFinite(Number(cell.ceiling)) ? Number(cell.ceiling) : floor;
  return viewerPosition.z >= floor - 0.01 && viewerPosition.z <= ceiling + 0.01;
}

export function visibleRoomCellsForViewers(encounterArr: Row, shape: PerceptionShape, viewers: Row[],
  lightSources: PerceptionSource[], darkSources: PerceptionSource[], obscuredSources: PerceptionSource[],
  context: PerceptionLightContext, field?: { floors: Record<string, PerceptionLightFloor> },
  explorationVerticalGate = false, cache?: PerceptionCache, physicalEpoch = ''): { roomIds: string[]; cells: VisibilityCell[] } {
  preparePerceptionCache(cache, physicalEpoch);
  const roomIds: string[] = []; const cells: VisibilityCell[] = [];
  const snapshots = viewers.map(viewer => sensesSnapshot(viewer, encounterArr));
  const rayZs = viewers.map(viewer => position(viewer).y === null ? null : sightRayZ(shape, viewer));
  const cellLight: Record<string, string> = {};
  for (const floor of Object.values(field?.floors ?? {})) for (const [id, tier] of Object.entries(floor.cellTiers)) cellLight[id] = lightFromTier(tier);
  const windowBoxIds = new Set(context.windows.map(window => window.boxId));
  const boxesById = new Map(shape.boxes.map(box => [String(box.id ?? '').trim(), box]));
  for (const box of shape.boxes) for (const cell of cachedVisibilityCells(box, cache, physicalEpoch)) {
    const x = (cell.x.min + cell.x.max) / 2; const y = (cell.y.min + cell.y.max) / 2;
    let sampleLight: string | undefined;
    if (cellLight[cell.id]) {
      let at = cache && physicalEpoch ? cache.boxAtByCellId.get(cell.id) : undefined;
      if (at === undefined) {
        at = boxAt(shape, { x, y, z: cell.floor });
        if (cache && physicalEpoch) cache.boxAtByCellId.set(cell.id, at);
      }
      const atId = String(at?.id ?? '').trim(); const roomId = cell.roomId.trim();
      const roomBox = boxesById.get(roomId);
      if (at && atId && roomId && (atId === roomId || (!windowBoxIds.has(atId) && !windowBoxIds.has(roomId) && roomBox
        && baseLightForBox(context, at, shape) === baseLightForBox(context, roomBox, shape)))) sampleLight = cellLight[cell.id];
    }
    const sample: Row = { position: { x_pos: x, y_pos: y, z_pos: cell.floor } };
    for (let index = 0; index < viewers.length; index += 1) {
      if (explorationVerticalGate && !explorationVerticalDiscoveryAllowed(shape, viewers[index]!, cell)) continue;
      if (sensedByViewer(encounterArr, shape, viewers[index]!, sample, lightSources, darkSources, obscuredSources,
        { context, senses: snapshots[index], lightAtTarget: sampleLight, viewerRayZ: rayZs[index] })) {
        roomIds.push(cell.roomId); cells.push(cell); break;
      }
    }
  }
  return { roomIds, cells };
}

function sortUnique(ids: string[]): string[] {
  return [...new Set(ids.filter(id => typeof id === 'string' && id !== ''))].sort();
}

/** A dependency record, never a copy of a creature, inventory row or encounter. */
function visibilityFacts(row: Row, encounterArr: Row, shape: PerceptionShape): string {
  return JSON.stringify([position(row), entityId(row, 'creature'), entityId(row, 'inventory'),
    privateRow(row, 'creature'), privateRow(row, 'inventory'), rowScalar(row, 'possess_id'),
    reachSizeFt(row), reachOf(row) ?? numberOf(ruleLayerValue(row, 'reach'), 5), sizeWidthFt(row),
    sightRayZ(shape, row), targetGrounded(shape, row), rowScalar(row, 'inventory_generation'),
    sensesSnapshot(row, encounterArr), seeThroughWallsFt(row, encounterArr), positionKnown(row),
    hasStealthStatus(row, PERCEPTION_STATUS.hidden),
    ...[PERCEPTION_STATUS.invisible, PERCEPTION_STATUS.greaterInvisible, PERCEPTION_STATUS.ethereal,
      PERCEPTION_STATUS.obscured].map(id => hasStatus(row, id)),
    ...['not_detected_by_special_senses', 'not_detected_by_truesight', 'bright_light_dim_light']
      .map(key => ruleLayerValue(row, key, encounterArr)),
    activeStatuses(row).some(status => String(itemsCol(status, 'item_type')
      ?? itemsCol(statusDefinition(status, encounterArr), 'item_type') ?? '').toLowerCase() === 'spell')]);
}

function sameEntries<T>(left: T[], right: T[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function buildPerceptionProjection(input: PerceptionInput, cache?: PerceptionCache, collected?: CollectedSources): PerceptionResult {
  const encounterArr = input.encounterArr;
  requireInventoryFloor(encounterArr);
  const geometryKey = preparePerceptionGeometry(cache, input.shape);
  const creatures = rowsOf(encounterArr.encounter_creatures_arr).filter(row => entityId(row, 'creature') !== '');
  const inventories = rowsOf(encounterArr.encounter_inventories_arr).filter(row => entityId(row, 'inventory') !== ''
    && numberOf(rowScalar(row, 'inventory_deleted')) !== 1);
  const viewerLookup = input.viewerCreatureIds === null ? null
    : new Set(input.viewerCreatureIds.map(id => id.trim().toUpperCase()));
  const perceptionView = !input.canManage || viewerLookup !== null;
  const viewers = !perceptionView ? [] : creatures.filter(row => {
    if (!input.canManage && privateRow(row, 'creature')) return false;
    const id = entityId(row, 'creature').toUpperCase();
    return viewerLookup === null ? playerPermission(row) : viewerLookup.has(id);
  });
  const sources = collected ?? collectLightAndDarkSources(encounterArr, cache);
  const fixtures = fixtureLightSources(input.shape, input.fixtureLightRadius ?? 0, cache);
  const lightSources = [...sources.lightSources, ...fixtures];
  const context = lightContext(encounterArr, input.shape, input.light);
  const field = lightField(input.shape, context, lightSources, sources.darkSources, 5, cache, input.physicalEpoch);
  const viewerIds = sortUnique(viewers.map(viewer => entityId(viewer, 'creature')));
  const viewKey = JSON.stringify([perceptionView, input.canManage, viewerIds]);
  const encounterId = String(encounterField(encounterArr, 'encounter_id') ?? '');
  let held = cache?.held;
  if (!held || held.encounterId !== encounterId || held.viewKey !== viewKey) {
    held = { encounterId, viewKey, targets: new Map(), roomCounts: new Map(), roomsKey: '', rooms: { roomIds: [], cells: [] },
      result: { authority: perceptionView ? 'see-with-senses-plus-shape-los' : 'gm-complete', view: perceptionView ? 'player' : 'gm',
        viewerCreatureIds: viewerIds, visibleCreatureIds: [], visibleInventoryIds: [], visibleRoomIds: [],
        visibilityCellSizeFt: 5, visibleRoomCells: [], lightField: field } };
    if (cache) cache.held = held;
  }
  const result = held.result;
  result.lightField = field;
  const worldKey = JSON.stringify([geometryKey, input.physicalEpoch, context, lightSources, sources.darkSources, sources.obscuredSources]);
  const viewerKey = JSON.stringify(viewers.map(row => visibilityFacts(row, encounterArr, input.shape)));
  const roomsKey = JSON.stringify([worldKey, viewerKey]);
  let roomMembershipChanged = false;
  const countRooms = (rooms: string[], delta: number): void => {
    for (const id of rooms) {
      if (!id) continue;
      const count = held.roomCounts.get(id) ?? 0;
      const next = count + delta;
      if ((count > 0) !== (next > 0)) roomMembershipChanged = true;
      if (next > 0) held.roomCounts.set(id, next); else held.roomCounts.delete(id);
    }
  };
  const commonChanged = held.roomsKey !== roomsKey;
  if (commonChanged) {
    countRooms(held.rooms.roomIds, -1);
    held.rooms = perceptionView ? visibleRoomCellsForViewers(encounterArr, input.shape, viewers,
      lightSources, sources.darkSources, sources.obscuredSources, context, field, false, cache, input.physicalEpoch)
      : { roomIds: input.shape.boxes.map(box => String(box.id ?? '').trim()), cells: [] };
    countRooms(held.rooms.roomIds, 1);
    if (!sameEntries(result.visibleRoomCells, held.rooms.cells)) result.visibleRoomCells = held.rooms.cells;
    held.roomsKey = roomsKey;
  }
  const snapshots = viewers.map(viewer => sensesSnapshot(viewer, encounterArr));
  const present = new Set<string>();
  let creatureMembershipChanged = false; let inventoryMembershipChanged = false;
  for (const [kind, rows] of [['creature', creatures], ['inventory', inventories]] as const) {
    for (const target of rows) {
      const id = entityId(target, kind); const targetId = `${kind}:${id}`;
      present.add(targetId);
      const key = visibilityFacts(target, encounterArr, input.shape);
      const previous = held.targets.get(targetId);
      if (!commonChanged && previous?.key === key) continue;
      const self = kind === 'creature' && viewerIds.includes(id);
      const visible = !perceptionView || self || ((!privateRow(target, kind) || input.canManage)
        && ((kind === 'creature' && positionKnown(target)) || viewers.some((viewer, index) =>
          sensedByViewer(encounterArr, input.shape, viewer, target, lightSources, sources.darkSources, sources.obscuredSources,
            { context, senses: snapshots[index] }))));
      const rooms = perceptionView && visible ? roomIdsAt(input.shape, position(target)) : [];
      if (previous) countRooms(previous.rooms, -1);
      countRooms(rooms, 1);
      held.targets.set(targetId, { key, visible, rooms });
      if ((previous?.visible ?? false) !== visible) {
        if (kind === 'creature') creatureMembershipChanged = true; else inventoryMembershipChanged = true;
      }
    }
  }
  for (const [id, previous] of held.targets) if (!present.has(id)) {
    countRooms(previous.rooms, -1); held.targets.delete(id);
    if (previous.visible) { if (id.startsWith('creature:')) creatureMembershipChanged = true; else inventoryMembershipChanged = true; }
  }
  if (creatureMembershipChanged) result.visibleCreatureIds = sortUnique(creatures
    .filter(row => held.targets.get(`creature:${entityId(row, 'creature')}`)?.visible).map(row => entityId(row, 'creature')));
  if (inventoryMembershipChanged) result.visibleInventoryIds = sortUnique(inventories
    .filter(row => held.targets.get(`inventory:${entityId(row, 'inventory')}`)?.visible).map(row => entityId(row, 'inventory')));
  if (roomMembershipChanged) {
    const next = sortUnique([...held.roomCounts.keys()]);
    if (!sameEntries(result.visibleRoomIds, next)) result.visibleRoomIds = next;
  }
  return result;
}

function encodeBase64(bytes: number[]): string {
  if (typeof btoa === 'function') {
    let binary = '';
    for (let index = 0; index < bytes.length; index += 0x8000) {
      binary += String.fromCharCode(...bytes.slice(index, index + 0x8000));
    }
    return btoa(binary);
  }
  const nodeBuffer = (globalThis as { Buffer?: { from(input: number[]): { toString(encoding: string): string } } }).Buffer;
  if (nodeBuffer) return nodeBuffer.from(bytes).toString('base64');
  throw new Error('no base64 encoder available');
}

/** Server wire twin of ft3d_minimap_pack_light_grid(). */
export function packLightGrid(
  field: PerceptionResult['lightField'],
  complete: boolean,
  visibleCellIds: string[],
): Ft3dCellLightFloor[] {
  const visible = new Set(visibleCellIds);
  return Object.values(field.floors).map(floor => {
    const physical = complete ? [...floor.tiers] : new Array<number>(floor.tiers.length).fill(CELL_LIGHT_TIER.unseen);
    const display = [...physical];
    if (!complete) {
      for (const [cellId, index] of Object.entries(floor.cellIndex)) {
        if (!visible.has(cellId)) continue;
        let tier = floor.cellTiers[cellId] ?? floor.tiers[index] ?? CELL_LIGHT_TIER.unseen;
        if (physical[index] !== CELL_LIGHT_TIER.unseen) tier = Math.max(tier, physical[index]!);
        physical[index] = tier;
        display[index] = tier === CELL_LIGHT_TIER.pitchBlack || tier === CELL_LIGHT_TIER.magicalDarkness
          ? CELL_LIGHT_TIER.sensed
          : tier;
      }
    }
    return {
      floor: floor.floor,
      originX: floor.originX,
      originY: floor.originY,
      columns: floor.columns,
      rows: floor.rows,
      cellCount: Object.keys(floor.cellIndex).length,
      physical: encodeBase64(physical),
      display: encodeBase64(display),
    };
  });
}

function roundWire(value: number, digits: number): number {
  const scale = 10 ** digits;
  const rounded = Math.sign(value) * Math.round(Math.abs(value) * scale + Number.EPSILON) / scale;
  return Object.is(rounded, -0) || rounded === 0 ? 0 : rounded;
}

/** Observable entity-carried lights only; persistent AOE sources remain anonymous in the field. */
export function observableLightSources(
  encounterArr: Row,
  complete: boolean,
  visibleCreatureIds: string[],
  visibleInventoryIds: string[],
): ObservableLightSource[] {
  const creatureLookup = new Set(visibleCreatureIds.map(id => id.toUpperCase()));
  const inventoryLookup = new Set(visibleInventoryIds.map(id => id.toUpperCase()));
  const sources: ObservableLightSource[] = [];
  const push = (row: Row, kind: 'creature' | 'inventory'): void => {
    const id = entityId(row, kind).toUpperCase();
    if (!id || (!complete && !(kind === 'inventory' ? inventoryLookup : creatureLookup).has(id))) return;
    const radius = Math.abs(numberOf(ruleLayerValue(row, 'bright_light_dim_light', encounterArr)));
    const at = position(row);
    if (!(radius > 0)) return;
    sources.push({
      x: roundWire(at.x, 2),
      // Native position buckets omit zero scalars. The physical light field already
      // reads that omission as y=0; the rendered source must use the same origin.
      y: roundWire(at.y ?? 0, 2),
      z: roundWire(at.z, 2),
      radius: roundWire(radius, 2),
      magical: truthyPhp(ruleLayerValue(row, 'magical_light', encounterArr)),
    });
  };
  rowsOf(encounterArr.encounter_creatures_arr).forEach(row => push(row, 'creature'));
  rowsOf(encounterArr.encounter_inventories_arr)
    .filter(row => numberOf(rowScalar(row, 'inventory_deleted')) !== 1)
    .forEach(row => push(row, 'inventory'));
  sources.sort((left, right) => {
    const a = [left.x, left.y, left.z, left.radius, Number(left.magical)];
    const b = [right.x, right.y, right.z, right.radius, Number(right.magical)];
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] !== b[index]) return a[index]! < b[index]! ? -1 : 1;
    }
    return 0;
  });
  return sources;
}

export function observableFixtureSources(
  fixtureSources: PerceptionSource[],
  complete: boolean,
  visibleRoomCells: VisibilityCell[],
): ObservableLightSource[] {
  const sources: ObservableLightSource[] = [];
  for (const source of fixtureSources) {
    const x = Number(source.x);
    const y = Number(source.z);
    const elevation = numberOf(source.elevation);
    const observable = complete || visibleRoomCells.some(cell => (
      x >= cell.x.min - 0.01 && x <= cell.x.max + 0.01
      && y >= cell.y.min - 0.01 && y <= cell.y.max + 0.01
      && elevation >= cell.floor - 0.01 && elevation <= cell.ceiling + 0.01
    ));
    if (!observable) continue;
    sources.push({
      x: roundWire(x, 2),
      y: roundWire(y, 2),
      z: roundWire(elevation, 2),
      radius: roundWire(Number(source.radius), 2),
      magical: false,
    });
  }
  return sources;
}

function teamIdOf(row: Row, encounterArr: Row): string {
  const nested = recordOf(row.encounter_creatures);
  const value = encounterCreatureField(nested, 'team')
    ?? encounterCreatureField(row, 'team')
    ?? ruleLayerValue(row, 'team', encounterArr);
  return typeof value === 'string' ? value.trim() : '';
}

function explorationTeamViewpoints(encounterArr: Row, teamId: string): Row[] {
  if (!teamId.trim()) return [];
  return rowsOf(encounterArr.encounter_creatures_arr).filter(row => (
    entityId(row, 'creature') !== ''
    && teamIdOf(row, encounterArr) === teamId
    && !privateRow(row, 'creature')
    && numberOf(rowScalar(row, 'encounter_creature_dead')) !== 1
  )).sort((left, right) => {
    const a = entityId(left, 'creature').toUpperCase();
    const b = entityId(right, 'creature').toUpperCase();
    return a < b ? -1 : a > b ? 1 : 0;
  });
}

export interface TeamExplorationPreviewOptions {
  /** The current projection already read these held source facts. */
  sources?: CollectedSources;
  light?: PerceptionInput['light'];
  fixtureLightRadius?: number;
  cache?: PerceptionCache;
  physicalEpoch?: string;
}

/** Read-only current discovery input for the selected actor's canonical team. */
export async function buildTeamExplorationPreview(
  encounterArr: Row,
  shape: PerceptionShape,
  viewerId: string,
  options: TeamExplorationPreviewOptions = {},
): Promise<Ft3dTeamExplorationPreview | null> {
  const normalizedViewerId = viewerId.trim().toUpperCase();
  const geometryKey = preparePerceptionGeometry(options.cache, shape);
  requireInventoryFloor(encounterArr);
  if (!normalizedViewerId) return null;
  const selected = rowsOf(encounterArr.encounter_creatures_arr)
    .find(row => entityId(row, 'creature').toUpperCase() === normalizedViewerId) ?? null;
  const teamId = selected ? teamIdOf(selected, encounterArr) : '';
  if (!teamId) return null;
  const viewpoints = explorationTeamViewpoints(encounterArr, teamId);
  const viewpointCreatureIds = viewpoints.map(row => entityId(row, 'creature'));
  // Match PHP's union authority, including before a same-team cached preview is reused.
  if (!viewpointCreatureIds.some(id => id.toUpperCase() === normalizedViewerId)) return null;
  const collected = options.sources ?? collectLightAndDarkSources(encounterArr, options.cache);
  const fixtures = fixtureLightSources(shape, options.fixtureLightRadius ?? 0, options.cache);
  const lights = [...collected.lightSources, ...fixtures];
  const context = lightContext(encounterArr, shape, options.light);
  const previewCacheKey = options.cache ? JSON.stringify([
    teamId,
    geometryKey, context, lights, collected.darkSources, options.physicalEpoch ?? '',
    viewpoints.map(row => [entityId(row, 'creature'), visibilityFacts(row, encounterArr, shape)]),
    collected.obscuredSources,
  ]) : '';
  const heldPreview = previewCacheKey ? options.cache?.teamPreviews.get(previewCacheKey) : undefined;
  if (heldPreview) return heldPreview;
  const field = lightField(shape, context, lights, collected.darkSources, 5, options.cache, options.physicalEpoch);
  const visibility = visibleRoomCellsForViewers(encounterArr, shape, viewpoints, lights,
    collected.darkSources, collected.obscuredSources, context, field, true, options.cache, options.physicalEpoch);
  const registryShapeJson = JSON.stringify([shape.boxes]);
  const explorationShape = shape as unknown as Ft3dExplorationShape;
  const registry = options.cache?.explorationRegistry?.shapeJson === registryShapeJson
    ? options.cache.explorationRegistry.value
    : await buildExplorationCellRegistry(explorationShape);
  if (options.cache && options.cache.explorationRegistry?.shapeJson !== registryShapeJson) {
    options.cache.explorationRegistry = { shapeJson: registryShapeJson, value: registry };
  }
  const registryById = new Map(registry.cells.map(cell => [cell.id, cell]));
  const visibleRoomCells = visibility.cells.flatMap(cell => {
    const registered = registryById.get(cell.id);
    return registered ? [registered as Ft3dExplorationCell] : [];
  }).sort((left, right) => left.cellKey!.localeCompare(right.cellKey!));
  const preview: Ft3dTeamExplorationPreview = {
    schema: 'ft.ft3d.team-exploration-preview.v1',
    teamId,
    geometryRevision: registry.geometryRevision,
    cellSizeFt: registry.cellSizeFt,
    viewpointCreatureIds,
    visibleRoomCells: visibleRoomCells as unknown as Ft3dTeamExplorationPreview['visibleRoomCells'],
    persistence: 'session-only',
    queryOnly: true,
    writes: 0,
  };
  if (options.cache && previewCacheKey) {
    if (options.cache.teamPreviews.size >= 8) options.cache.teamPreviews.clear();
    options.cache.teamPreviews.set(previewCacheKey, preview);
  }
  return preview;
}

function epochSources(sources: PerceptionSource[]): Array<[number, number, number | null, number, number]> {
  return sources.map(source => ([
    roundWire(numberOf(source.x), 3),
    roundWire(numberOf(source.z), 3),
    source.elevation === null || source.elevation === undefined ? null : roundWire(numberOf(source.elevation), 3),
    roundWire(numberOf(source.radius), 3),
    truthyPhp(source.magical_light) ? 1 : 0,
  ] as [number, number, number | null, number, number])).sort((left, right) => {
    for (let index = 0; index < left.length; index += 1) {
      if (left[index] === right[index]) continue;
      if (left[index] === null) return -1;
      if (right[index] === null) return 1;
      return left[index]! < right[index]! ? -1 : 1;
    }
    return 0;
  });
}

async function digestHex(algorithm: 'SHA-1' | 'SHA-256', value: string): Promise<string> {
  const digest = await crypto.subtle.digest(algorithm, new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Exact PHP physical epoch contract (currently truncated SHA-1, unlike exploration's SHA-256 keys). */
export async function physicalLightEpoch(
  shape: PerceptionShape,
  context: PerceptionLightContext,
  lightSources: PerceptionSource[],
  darkSources: PerceptionSource[],
  fixtureLightRadius: number,
  fixtureCount: number,
  doorStates: Record<string, { open: boolean; revision: number }> | [],
): Promise<string> {
  const payload = {
    // v5 is the current server field contract, including numeric floor groups. The version
    // applies to every shape so a cached v4 field can never masquerade as the current grid.
    v: 5,
    override: context.override,
    effective: context.effective,
    underground: context.underground,
    shape: [shape.boxes ?? [], shape.openings ?? []],
    doors: doorStates,
    fixtures: [1, '212F2A24-3F66-AC79-DB6A-F97487DA9680', fixtureLightRadius, fixtureCount],
    light: epochSources(lightSources),
    dark: epochSources(darkSources),
  };
  return (await digestHex('SHA-1', JSON.stringify(payload))).slice(0, 16);
}

function nextClientLightRevision(cache: PerceptionCache, viewKey: string, minimum: number): number {
  const previous = cache.revisionsByView.get(viewKey) ?? 0;
  const revision = Math.max(previous + 1, Math.max(0, Math.floor(minimum)) + 1);
  cache.revisionsByView.set(viewKey, revision);
  return revision;
}

/**
 * The server's revision is monotonic per view AND stable while the epoch is unchanged
 * (ft3d_minimap_light_revision memoises per epoch). The engine relies on that: its perception field
 * signature includes the revision, and an unchanged signature is its no-op path (no veil teardown, no
 * post-process rebuild, no texture upload). A fresh number on every local compute defeated that and
 * rebuilt the veil on every delta (CPU profile 2026-09-04). Re-issue the SAME revision for the same
 * (view, epoch); a new epoch claims the next number.
 */
const clientLightRevisionByEpoch = new WeakMap<PerceptionCache, Map<string, { epoch: string; revision: number }>>();
function stableClientLightRevision(cache: PerceptionCache, viewKey: string, epoch: string, minimum: number): number {
  let byView = clientLightRevisionByEpoch.get(cache);
  if (!byView) { byView = new Map(); clientLightRevisionByEpoch.set(cache, byView); }
  const held = byView.get(viewKey);
  // `>=`: the engine feeds back the revision it last ACCEPTED as the minimum — usually this very number —
  // and acceptCellLightRevision allows equality, so equal is "still current", not "advance".
  if (held && held.epoch === epoch && held.revision >= Math.floor(minimum)) return held.revision;
  const revision = nextClientLightRevision(cache, viewKey, minimum);
  byView.set(viewKey, { epoch, revision });
  return revision;
}

/**
 * Once the client issues its own light-grid revisions, it OWNS the per-view sequence: a later SERVER
 * receipt (the 60 s catch-up, the fallback, a door toggle) carries a Redis-issued number that is almost
 * always below the client's counter, and `acceptCellLightRevision` would reject it as out of order.
 * Re-issue the server grid's revision through the same counter before applying it. Returns the grid
 * unchanged when it carries no revision.
 */
export function claimServerLightRevision<T extends { view?: string; revision?: number } | null | undefined>(
  cache: PerceptionCache,
  grid: T,
  viewerCreatureIds: string[],
): T {
  if (!grid || typeof grid !== 'object' || !Number.isFinite(Number(grid.revision))) return grid;
  const viewKey = grid.view === 'gm' ? 'gm' : `player:${[...viewerCreatureIds].sort().join(',')}`;
  grid.revision = nextClientLightRevision(cache, viewKey, Number(grid.revision));
  return grid;
}

/** Assemble the complete server-compatible wire projection consumed by the FT3D engine. */
export async function buildClientPerceptionProjection(
  input: ClientPerceptionInput,
  cache: PerceptionCache,
): Promise<Ft3dMinimapVisibility> {
  // Digest/registry work yields. Serialize this controller's local computations so a superseded
  // view cannot mutate held results after its successor has been applied.
  const flight = (cache.flight ?? Promise.resolve()).catch(() => undefined)
    .then(() => buildClientProjection(input, cache));
  cache.flight = flight;
  try { return await flight; }
  finally { if (cache.flight === flight) cache.flight = null; }
}

async function buildClientProjection(input: ClientPerceptionInput, cache: PerceptionCache): Promise<Ft3dMinimapVisibility> {
  preparePerceptionGeometry(cache, input.shape);
  const collected = collectLightAndDarkSources(input.encounterArr, cache);
  const fixtures = fixtureLightSources(input.shape, input.fixtureLightRadius ?? 0, cache);
  const lights = [...collected.lightSources, ...fixtures];
  const context = lightContext(input.encounterArr, input.shape, input.light);
  const doorEntries = Object.entries(input.doorStates ?? {}).map(([openingId, state]) => [
    openingId,
    { open: state?.open === true, revision: Math.max(0, Number(state?.revision) || 0), cleared: state?.cleared === true,
      ...(state?.windowPhase ? { windowPhase: state.windowPhase } : {}) },
  ] as const);
  // PHP encodes an empty projected associative array as [] and a populated one as an object. The
  // distinction is hash-visible even though consumers treat both as an empty lookup.
  const doorStates: Record<string, { open: boolean; revision: number }> | [] = doorEntries.length > 0
    ? Object.fromEntries(doorEntries)
    : [];
  const physicalEpoch = await physicalLightEpoch(input.shape, context, lights, collected.darkSources,
    input.fixtureLightRadius ?? 0, fixtures.length, doorStates);
  const base = buildPerceptionProjection({ ...input, physicalEpoch }, cache, collected);
  // Keyhole peek: the held server block joins BEFORE packing, so its cells carry their real light tier.
  const result = input.serverPeek ? mergeServerPeek(base, input.serverPeek.block, input.serverPeek.current) : base;
  const peekMerged = result !== base;
  const complete = result.view === 'gm';
  if (!cache.packed || cache.packed.field !== result.lightField || cache.packed.cells !== result.visibleRoomCells
    || cache.packed.complete !== complete) {
    cache.packed = { field: result.lightField, cells: result.visibleRoomCells, complete,
      value: packLightGrid(result.lightField, complete, result.visibleRoomCells.map(cell => cell.id)) };
  }
  const packedFloors = cache.packed.value;
  const observableKey = JSON.stringify([complete, cache.sources?.key, cache.geometryKey, input.fixtureLightRadius ?? 0]);
  if (!cache.observable || cache.observable.key !== observableKey
    || cache.observable.creatures !== result.visibleCreatureIds || cache.observable.inventories !== result.visibleInventoryIds
    || cache.observable.cells !== result.visibleRoomCells) {
    const value = [...observableLightSources(input.encounterArr, complete, result.visibleCreatureIds, result.visibleInventoryIds),
      ...observableFixtureSources(fixtures, complete, result.visibleRoomCells)];
    const previous = cache.observable?.value;
    cache.observable = { key: observableKey, creatures: result.visibleCreatureIds, inventories: result.visibleInventoryIds,
      cells: result.visibleRoomCells, value: previous && JSON.stringify(previous) === JSON.stringify(value) ? previous : value };
  }
  const observable = cache.observable.value;
  const viewKey = complete ? 'gm' : `player:${result.viewerCreatureIds.join(',')}`;
  const wireKey = JSON.stringify([viewKey, physicalEpoch, complete ? context : null]);
  if (!cache.wire || cache.wire.key !== wireKey || cache.wire.packed !== packedFloors || cache.wire.sources !== observable) {
    const epoch = complete ? physicalEpoch
      : (await digestHex('SHA-1', `player|${JSON.stringify([result.viewerCreatureIds, packedFloors,
        observableLightSources(input.encounterArr, complete, result.visibleCreatureIds, result.visibleInventoryIds)])}`)).slice(0, 16);
    const lightGrid = {
      schema: 'ft.ft3d.cell-light-grid.v1' as const,
      view: result.view,
      revision: 0,
      revisionSource: 'client' as const,
      epoch,
      cellSizeFt: 5,
      tiers: {
        unseen: CELL_LIGHT_TIER.unseen,
        pitch_black: CELL_LIGHT_TIER.pitchBlack,
        magical_darkness: CELL_LIGHT_TIER.magicalDarkness,
        dim: CELL_LIGHT_TIER.dim,
        bright: CELL_LIGHT_TIER.bright,
        sensed: CELL_LIGHT_TIER.sensed,
      },
      ...(complete ? { baseLight: {
        override: context.override,
        effective: context.effective,
        authoredRooms: context.authored,
        daylightWindows: context.windows.length,
      } } : {}),
      floors: packedFloors,
      sources: observable,
      queryOnly: true as const,
      writes: 0 as const,
    };
    cache.wire = { key: wireKey, packed: packedFloors, sources: observable, epoch, grid: lightGrid };
  }
  const lightGrid = cache.wire.grid;
  lightGrid.revision = stableClientLightRevision(cache, viewKey, cache.wire.epoch, input.minimumLightRevision ?? 0);
  const projection: Ft3dMinimapVisibility = cache.client ?? {
    schema: 'ft.ft3d.semantic-minimap-visibility.v1',
    authority: result.authority,
    view: result.view,
    viewerCreatureIds: result.viewerCreatureIds,
    visibleCreatureIds: result.visibleCreatureIds,
    visibleInventoryIds: result.visibleInventoryIds,
    visibleRoomIds: result.visibleRoomIds,
    visibilityCellSizeFt: 5,
    visibleRoomCells: result.visibleRoomCells,
    lightGrid,
    doorStates: doorStates as Record<string, { open: boolean; revision: number }>,
    exploredStateUsed: false,
    queryOnly: true,
    writes: 0,
    ...(input.shapeSource ? { shapeSource: input.shapeSource } : {}),
    ...(peekMerged && input.serverPeek ? { peek: input.serverPeek.block } : {}),
  };
  Object.assign(projection, { authority: result.authority, view: result.view, viewerCreatureIds: result.viewerCreatureIds,
    visibleCreatureIds: result.visibleCreatureIds, visibleInventoryIds: result.visibleInventoryIds,
    visibleRoomIds: result.visibleRoomIds, visibleRoomCells: result.visibleRoomCells,
    lightGrid, doorStates: JSON.stringify(projection.doorStates) === JSON.stringify(doorStates) ? projection.doorStates : doorStates });
  delete projection.peek; delete projection.peeks; delete projection.shapeSource; delete projection.exploredRoomCells;
  delete projection.teamExplorationPreview; delete projection.teamExplorationState;
  if (input.shapeSource) projection.shapeSource = input.shapeSource;
  if (peekMerged && input.serverPeek) projection.peek = input.serverPeek.block;
  cache.client = projection;
  if (!complete && result.viewerCreatureIds[0]) {
    const preview = await buildTeamExplorationPreview(input.encounterArr, input.shape, result.viewerCreatureIds[0], {
      light: input.light,
      fixtureLightRadius: input.fixtureLightRadius,
      cache,
      physicalEpoch, sources: collected,
    });
    if (preview) {
      projection.teamExplorationPreview = preview;
      if (input.teamExplorationState
        && input.teamExplorationState.teamId.trim().toUpperCase() === preview.teamId.trim().toUpperCase()
        && input.teamExplorationState.geometryRevision.toLowerCase() === preview.geometryRevision.toLowerCase()) {
        projection.teamExplorationState = input.teamExplorationState;
      }
    }
  }
  return projection;
}
